import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import Navigation from "../../components/Navigation/Navigation";
import { NotificationList } from "../../components/notification/NotificationList";
import { useNotification } from "../../components/notification/useNotification";
import { $apiV1, $apiV2 } from "../../config/api";
import DateRangeSlider from "../../components/Events/ChooseSeasonSelector/DateRangeSlider";
import { openVideoList } from "../../helpers/service";
import useOutsideClick from "../../hooks/useOutsideClick";
import { MainWrapper } from "../../style/DasboardMain";
import { breakpoints } from "../../style/size";
import {
  createMatchesCountOptions,
  deriveSelectedMatches,
  filterMatchesByDateRange,
  getSelectedMatchIds,
} from "../tournametns/team/helpers/pageFilters";
import {
  buildSequenceEventOptionsFromApiMarkers,
  buildSequenceMarkerPairTitle,
  buildSequenceSearchPayload,
  buildSequenceTemplateFilters,
  createSequenceStepFromEventOption,
  getResultDuration,
  groupSequenceResultsByMatch,
  hasSequenceTemplateContent,
  moveItem,
  parseSequenceTemplateFilters,
} from "./sequenceSearchUtils";
import { useSavedFilterTemplates } from "../../hooks/useSavedFilterTemplates";
import { SavedFilterTemplatesControlsView } from "../../components/filterTemplates/SavedFilterTemplatesControls";
import {
  TemplateControls,
  TemplateControlsSection,
  TemplateDropdown,
  TemplateDropdownButton,
  TemplateDropdownButtonText,
} from "../../style/CompareWorkspaceStyle";
import PlayerRecruitmentSequences, {
  hasPlayerRecruitmentTemplateContent,
  PLAYER_RECRUITMENT_TEMPLATE_PAGE_KEY,
} from "./PlayerRecruitmentSequences";

const DEFAULT_PAGE_SIZE = 15;
const RESULTS_PAGE_SIZE = 10;
const SEQUENCE_FETCH_PAGE_SIZE = 100;
const SEQUENCE_TEMPLATE_PAGE_KEY = "sequence_search";
const CUSTOM_MATCH_PRESET_ID = "custom";
const SEQUENCE_VIDEO_MODE_SEPARATE = "separate";
const SEQUENCE_VIDEO_MODE_SEQUENCE = "sequence";
const SEQUENCE_SEARCH_SCOPE_TOO_BROAD_ERROR =
  "sequence_search_scope_too_broad";
// Top-level form tabs. Only "sequences" carries the existing search form today;
// the other two are placeholders until their designs land.
const SEQUENCE_TAB_SEQUENCES = "sequences";
const SEQUENCE_TAB_OPPONENT = "opponent";
const SEQUENCE_TAB_SIMILARITIES = "similarities";
const SEQUENCE_TAB_PLAYER_RECRUITMENT = "player_recruitment";
const identityOptions = (items) => items;

const SEQUENCE_TUTORIAL_STORAGE_KEY = "sequence_search_tutorial_seen_v1";
const _isLightTheme = localStorage.getItem("theme") === "light";
const SEQUENCE_TUTORIAL_ACCENT = _isLightTheme ? "#2363F7" : "#BEFF00";
const SEQUENCE_TUTORIAL_CARD_BG = _isLightTheme
  ? "#ffffff"
  : "linear-gradient(150deg, #1e2e14, #111a0b)";
const SEQUENCE_TUTORIAL_TITLE_COLOR = _isLightTheme ? "#0f1117" : "#ffffff";
const SEQUENCE_TUTORIAL_BODY_COLOR = _isLightTheme ? "#6B7280" : "#8a9e80";
const SEQUENCE_TUTORIAL_SKIP_COLOR = _isLightTheme ? "#9CA3AF" : "#5a6e50";
const SEQUENCE_TUTORIAL_BTN_TEXT = _isLightTheme ? "#ffffff" : "#0a0e05";
const SEQUENCE_TUTORIAL_STEP_INACTIVE = _isLightTheme
  ? "#E5E7EB"
  : "rgba(255,255,255,0.12)";
const SEQUENCE_TUTORIAL_FONT_BASE = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SEQUENCE_TUTORIAL_FONT_MEDIUM = '"Satoshi Medium", "Satoshi-Regular", sans-serif';
const SEQUENCE_TUTORIAL_FONT_BOLD = '"SatoshiBold", "Satoshi-Bold", sans-serif';
const SEQUENCE_TUTORIAL_OVERLAY_Z_INDEX = 100050;
const SEQUENCE_TUTORIAL_CARD_Z_INDEX = 100060;
const SEQUENCE_HOW_IT_WORKS_Z_INDEX = 900;

// Example search response shown inside the results panel while the tutorial
// runs, so the results step has something concrete to explain. Replaced by
// real results as soon as the user searches.
const SEQUENCE_TUTORIAL_DEMO_RESPONSE = {
  results: [
    {
      match: {
        id: 90001,
        home_team: "Riverton FC",
        away_team: "Eastfield United",
        score: "2-1",
        competition: "Demo League",
        date: "2026-04-12T18:00:00.000Z",
      },
      sequence_id: 481,
      sequence_ids: [481],
      duration_seconds: 14,
      matched_steps: [
        {
          index: 0,
          sequence_id: 481,
          kind: "Pass forward +",
          first_second: 612,
          last_second: 615,
          marker_ids: [90101],
        },
        {
          index: 1,
          sequence_id: 481,
          kind: "Goal",
          first_second: 624,
          last_second: 626,
          marker_ids: [90103],
        },
      ],
      markers: [
        {
          id: 90101,
          action: "Pass forward +",
          second: "612.000000",
          creator_id: 71,
          creator_team_id: 9101,
        },
        {
          id: 90102,
          action: "Cross +",
          second: "617.000000",
          creator_id: 72,
          creator_team_id: 9101,
        },
        {
          id: 90103,
          action: "Goal",
          second: "624.000000",
          creator_id: 73,
          creator_team_id: 9101,
        },
      ],
      players: [
        { id: 71, display_name: "D. Carter", player_in_squad_id: 71 },
        { id: 72, display_name: "L. Moreno", player_in_squad_id: 72 },
        { id: 73, display_name: "S. Novak", player_in_squad_id: 73 },
      ],
    },
    {
      match: {
        id: 90001,
        home_team: "Riverton FC",
        away_team: "Eastfield United",
        score: "2-1",
        competition: "Demo League",
        date: "2026-04-12T18:00:00.000Z",
      },
      sequence_id: 522,
      sequence_ids: [522],
      duration_seconds: 9,
      matched_steps: [
        {
          index: 0,
          sequence_id: 522,
          kind: "Pass forward +",
          first_second: 1485,
          last_second: 1487,
          marker_ids: [90201],
        },
        {
          index: 1,
          sequence_id: 522,
          kind: "Goal",
          first_second: 1492,
          last_second: 1494,
          marker_ids: [90202],
        },
      ],
      markers: [
        {
          id: 90201,
          action: "Pass forward +",
          second: "1485.000000",
          creator_id: 72,
          creator_team_id: 9101,
        },
        {
          id: 90202,
          action: "Goal",
          second: "1492.000000",
          creator_id: 71,
          creator_team_id: 9101,
        },
      ],
      players: [
        { id: 71, display_name: "D. Carter", player_in_squad_id: 71 },
        { id: 72, display_name: "L. Moreno", player_in_squad_id: 72 },
      ],
    },
  ],
};

const buildSequenceTutorialSteps = (t) => [
  {
    id: "welcome",
    spotlight: null,
    title: t("sequenceSearch.tutorialWelcomeTitle"),
    body: t("sequenceSearch.tutorialWelcomeBody"),
    position: "center",
  },
  {
    id: "tabs",
    spotlight: "tabs",
    title: t("sequenceSearch.tutorialTabsTitle"),
    body: t("sequenceSearch.tutorialTabsBody"),
    position: "below",
  },
  {
    id: "scope",
    spotlight: "scope",
    title: t("sequenceSearch.tutorialScopeTitle"),
    body: t("sequenceSearch.tutorialScopeBody"),
    position: "below",
  },
  {
    id: "matches",
    spotlight: "matches",
    title: t("sequenceSearch.tutorialMatchesTitle"),
    body: t("sequenceSearch.tutorialMatchesBody"),
    position: "below",
  },
  {
    id: "video-mode",
    spotlight: "videoMode",
    title: t("sequenceSearch.tutorialVideoModeTitle"),
    body: t("sequenceSearch.tutorialVideoModeBody"),
    position: "below",
  },
  {
    id: "timeline",
    spotlight: "timeline",
    title: t("sequenceSearch.tutorialTimelineTitle"),
    body: t("sequenceSearch.tutorialTimelineBody"),
    position: "above",
  },
  {
    id: "templates",
    spotlight: "templates",
    title: t("sequenceSearch.tutorialTemplatesTitle"),
    body: t("sequenceSearch.tutorialTemplatesBody"),
    position: "below",
  },
  {
    id: "results",
    spotlight: "results",
    title: t("sequenceSearch.tutorialResultsTitle"),
    body: t("sequenceSearch.tutorialResultsBody"),
    position: "center",
  },
];

const buildSequenceTutorialDemoSteps = () => [
  createSequenceStepFromEventOption(
    {
      id: "tutorial-pass-forward",
      step_id: 35,
      step_id_field: "action_ids",
      label: "Pass forward +",
      group: "Passing",
      kind: "pass_forward_plus",
    },
    "tutorial-step-pass-forward",
    "selected_team"
  ),
  createSequenceStepFromEventOption(
    {
      id: "tutorial-goal",
      step_id: 74,
      step_id_field: "action_ids",
      label: "Goal",
      group: "Shooting",
      kind: "goal",
    },
    "tutorial-step-goal",
    "selected_team"
  ),
];

const buildRectSnapshot = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
  right: rect.right,
  bottom: rect.bottom,
});

const mergeElementRects = (elements) => {
  const rects = elements
    .filter(Boolean)
    .map((element) => element.getBoundingClientRect())
    .filter((rect) => rect.width > 0 && rect.height > 0);

  if (!rects.length) {
    return null;
  }

  const left = Math.min(...rects.map((rect) => rect.left));
  const top = Math.min(...rects.map((rect) => rect.top));
  const right = Math.max(...rects.map((rect) => rect.right));
  const bottom = Math.max(...rects.map((rect) => rect.bottom));

  return buildRectSnapshot({
    left,
    top,
    width: right - left,
    height: bottom - top,
    right,
    bottom,
  });
};

const createStepId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

// Preset example sequences for the "Load example" menu, surfaced as
// Example 1-4. Each preset is a plain list of step descriptors (action id +
// field + which team it belongs to); buildExampleSequenceSteps turns them into
// real form steps. Every descriptor carries a user-facing label so the timeline
// explains the play instead of falling back to the raw "<field>: <id>" value.
const EXAMPLE_SEQUENCE_PRESET_KEYS = ["1", "2", "3", "4"];

const EXAMPLE_SEQUENCE_PRESET_STEPS = {
  "1": [
    {
      stepId: 71,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Passes Onward +",
      group: "Passing",
      kind: "passes_onward_plus",
    },
    {
      stepId: 74,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Goal",
      group: "Shooting",
      kind: "goal",
    },
  ],
  "2": [
    {
      stepId: 159,
      field: "action_ids",
      teamLink: "opponent",
      label: "Lost ball",
      group: "Possession",
    },
    {
      stepId: 36,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Pass",
      group: "Passing",
    },
    {
      stepId: 113,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Dribble",
      group: "Dribbling",
    },
    {
      stepId: 51,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Cross",
      group: "Crossing",
    },
    {
      stepId: 25,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Shot on target",
      group: "Shooting",
    },
  ],
  "3": [
    {
      stepId: 51,
      field: "event_action_ids",
      teamLink: "opponent",
      label: "Foul",
      group: "Discipline",
    },
    {
      stepId: 33,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Shots from Free kick",
      group: "Set pieces",
    },
  ],
  "4": [
    {
      stepId: 68,
      field: "action_ids",
      teamLink: "opponent",
      label: "Long Passes -",
      group: "Passing",
    },
    {
      stepId: 128,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Aerial Duels +",
      group: "Duels",
    },
    {
      stepId: 66,
      field: "action_ids",
      teamLink: "selected_team",
      label: "Long Passes +",
      group: "Passing",
    },
    {
      stepId: 65,
      field: "event_action_ids",
      teamLink: "selected_team",
      label: "Goal",
      group: "Shooting",
    },
  ],
};

const buildExampleSequenceSteps = (preset = "1") =>
  (EXAMPLE_SEQUENCE_PRESET_STEPS[preset] || []).map(
    ({ stepId, field, teamLink, label = "", group = "", kind }) =>
      createSequenceStepFromEventOption(
        {
          id: `example-${preset}-${teamLink}-${stepId}`,
          step_id: stepId,
          step_id_field: field === "event_action_ids" ? "action_ids" : field,
          type: field === "event_action_ids" ? "action" : "parameter",
          label,
          group,
          ...(kind ? { kind } : {}),
        },
        createStepId(),
        teamLink
      )
  );

const getOptionId = (option) =>
  option?.id ?? option?.competition_id ?? option?.player_id ?? option?.team_id;

const getSeasonId = (option) =>
  option?.season_id ?? option?.year_season_id ?? option?.id;

const getCompetitionId = (option) =>
  option?.competition_id ?? option?.competition__id ?? option?.id;

const getCompetitionLabel = (option) =>
  option?.name || option?.title || option?.competition || option?.display_name;

const getSeasonLabel = (option) =>
  option?.years || option?.year_season || option?.name || option?.title;

const getTeamLabel = (option) =>
  option?.name || option?.display_name || option?.team || option?.title;

const getPlayerLabel = (option) =>
  option?.display_name ||
  option?.full_name ||
  `${option?.name || ""} ${option?.surname || ""}`.trim() ||
  option?.name;

const getSeasonEndYear = (option) => {
  const directYear = Number(
    option?.end_year ??
      option?.year_end ??
      option?.season_end_year ??
      option?.to_year
  );
  if (Number.isFinite(directYear)) {
    return directYear;
  }

  const label = String(
    option?.years || option?.year_season || option?.name || option?.title || ""
  );
  const yearMatches = label.match(/\d{2,4}/g);
  if (!yearMatches?.length) {
    return 0;
  }

  const startYear = Number(yearMatches[0]);
  const lastYear = Number(yearMatches[yearMatches.length - 1]);
  if (!Number.isFinite(startYear) || !Number.isFinite(lastYear)) {
    return 0;
  }
  if (lastYear < 100 && startYear >= 1900) {
    return Math.floor(startYear / 100) * 100 + lastYear;
  }

  return lastYear;
};

const getSubmitErrorMessage = (error, t) => {
  const responseData = error?.response?.data;

  if (
    error?.response?.status === 422 &&
    responseData?.error === SEQUENCE_SEARCH_SCOPE_TOO_BROAD_ERROR
  ) {
    return t("sequenceSearch.scopeTooBroad");
  }

  return responseData?.message || t("sequenceSearch.submitError");
};

export const filterAvailableSeasonOptions = (options = []) => {
  const currentYear = new Date().getFullYear();
  const seenIds = new Set();
  const validOptions = (Array.isArray(options) ? options : []).filter(
    (option) => {
      const seasonId = Number(getSeasonId(option));
      const endYear = getSeasonEndYear(option);
      if (!Number.isFinite(seasonId) || (endYear && endYear > currentYear)) {
        return false;
      }
      if (seenIds.has(seasonId)) {
        return false;
      }
      seenIds.add(seasonId);
      return true;
    }
  );

  if (validOptions.length === 0) {
    return [];
  }

  const sorted = [...validOptions].sort((left, right) => {
    const yearDiff = getSeasonEndYear(right) - getSeasonEndYear(left);
    if (yearDiff !== 0) {
      return yearDiff;
    }

    return Number(getSeasonId(right) || 0) - Number(getSeasonId(left) || 0);
  });

  const seenLabels = new Set();
  return sorted.filter((option) => {
    const label = getSeasonLabel(option);
    if (!label || seenLabels.has(label)) {
      return false;
    }
    seenLabels.add(label);
    return true;
  });
};

const buildTeamSeasonOptions = (teamStats = {}, selectedCompetitionId) => {
  const availableSeasons = Array.isArray(teamStats?.available_seasons)
    ? teamStats.available_seasons
    : [];

  if (!selectedCompetitionId) {
    return availableSeasons;
  }

  const matchingSeasonIds = new Set(
    (Array.isArray(teamStats?.matches_played) ? teamStats.matches_played : [])
      .filter(
        (match) =>
          Number(getCompetitionId(match)) === Number(selectedCompetitionId)
      )
      .map((match) => Number(getSeasonId(match)))
      .filter((seasonId) => Number.isFinite(seasonId))
  );

  if (matchingSeasonIds.size === 0) {
    return [];
  }

  return availableSeasons.filter((seasonOption) =>
    matchingSeasonIds.has(Number(getSeasonId(seasonOption)))
  );
};

const formatCompactValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) =>
        typeof item === "object" && item !== null
          ? item.name || item.display_name || item.id || JSON.stringify(item)
          : String(item)
      )
      .join(", ");
  }

  if (value && typeof value === "object") {
    return value.name || value.display_name || value.id || JSON.stringify(value);
  }

  if (value === undefined || value === null || value === "") {
    return "-";
  }

  return String(value);
};

const formatEntityName = (value) => {
  if (Array.isArray(value)) {
    return value.map(formatEntityName).filter(Boolean).join(", ");
  }

  if (value && typeof value === "object") {
    return (
      value.display_name ||
      value.name ||
      value.title ||
      value.team_name ||
      value.short_name ||
      value.id ||
      ""
    );
  }

  if (value === undefined || value === null || value === "") {
    return "";
  }

  return String(value);
};

const formatDateValue = (value) => {
  if (!value) {
    return "";
  }

  const parsedDate = new Date(value);
  if (!Number.isFinite(parsedDate.getTime())) {
    return String(value);
  }

  return parsedDate.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatMatchTitle = (match = {}, fallbackLabel) => {
  const teams = [formatEntityName(match.home_team), formatEntityName(match.away_team)]
    .filter(Boolean)
    .join(" - ");
  if (teams && match.score) {
    return `${teams} (${match.score})`;
  }
  if (teams) {
    return teams;
  }
  return fallbackLabel;
};

const formatMatchScore = (match = {}) => {
  if (match?.home_team_score != null && match?.away_team_score != null) {
    return `${match.home_team_score} : ${match.away_team_score}`;
  }

  const rawScore = String(match?.score ?? "").trim();
  if (!rawScore) {
    return "";
  }

  const compactScore = rawScore.replace(/\s+/g, "");
  const separator = compactScore.includes(":")
    ? ":"
    : compactScore.includes("-")
      ? "-"
      : null;

  if (!separator) {
    return rawScore;
  }

  const [homeScore, awayScore] = compactScore.split(separator);
  if (homeScore == null || awayScore == null) {
    return rawScore;
  }

  return `${homeScore} : ${awayScore}`;
};

const formatMatchSelectLabel = (match = {}) => {
  const teams = [formatEntityName(match.home_team), formatEntityName(match.away_team)]
    .filter(Boolean)
    .join(" - ");
  const score = formatMatchScore(match);
  const matchTitle = [teams, score].filter(Boolean).join(" ");
  return [
    matchTitle || `#${match.id}`,
    formatEntityName(match.competition),
    formatDateValue(match.date),
  ]
    .filter(Boolean)
    .join(" | ");
};

const normalizeAvailableMatch = (match = {}) => ({
  ...match,
  id: match.id ?? match.match_id,
  competition_id: getCompetitionId(match),
  competition: match.competition || match.competition_name,
  date: match.date || match.match_date,
});

const normalizeIdList = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeIdList(item))
      .filter((item) => item !== "");
  }

  if (value && typeof value === "object") {
    return normalizeIdList(value.id ?? value.marker_id ?? value.event_id);
  }

  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (value === undefined || value === null || value === "") {
    return [];
  }

  return [String(value)];
};

const uniqueList = (items = []) => Array.from(new Set(items.filter(Boolean)));

const getMarkerId = (marker) =>
  normalizeIdList(
    marker && typeof marker === "object"
      ? marker.id ?? marker.marker_id ?? marker.event_id
      : marker
  )?.[0] || "";

const getResultMarkerRecords = (result = {}) => {
  const markerRecords = [];
  const seenMarkerIds = new Set();

  (Array.isArray(result.markers) ? result.markers : []).forEach((marker) => {
    const markerId = getMarkerId(marker);
    if (!markerId || seenMarkerIds.has(markerId)) {
      return;
    }

    seenMarkerIds.add(markerId);
    markerRecords.push(
      marker && typeof marker === "object" ? marker : { id: markerId }
    );
  });

  getMatchedSteps(result).forEach((step) => {
    normalizeIdList(step.marker_ids || step.markers).forEach((markerId) => {
      if (seenMarkerIds.has(markerId)) {
        return;
      }

      seenMarkerIds.add(markerId);
      markerRecords.push({ id: markerId });
    });
  });

  return markerRecords;
};

const getMatchedSteps = (result = {}) => {
  const rawSteps = result.matched_steps || result.steps || result.matches || [];
  return Array.isArray(rawSteps) ? rawSteps : [];
};

const getResultMarkers = (result = {}) => {
  const directMarkers = normalizeIdList(result.markers || result.marker_ids);
  const stepMarkers = getMatchedSteps(result).flatMap((step) =>
    normalizeIdList(step.marker_ids || step.markers)
  );

  return uniqueList([...directMarkers, ...stepMarkers]);
};

const getResultPlayers = (result = {}) =>
  uniqueList(
    (Array.isArray(result.players) ? result.players : [])
      .map(formatEntityName)
      .filter(Boolean)
  );

const findPlayerBySquadId = (players = [], playerSquadId) => {
  const normalizedSquadId = String(playerSquadId ?? "");
  if (!normalizedSquadId) {
    return null;
  }

  return (Array.isArray(players) ? players : []).find(
    (player) => String(player?.player_in_squad_id ?? "") === normalizedSquadId
  );
};

const getMarkerPlayerLabel = (marker = {}, players = []) => {
  const creatorPlayer = findPlayerBySquadId(players, marker.creator_id);
  const recipientPlayer = findPlayerBySquadId(players, marker.recipient_id);

  if (creatorPlayer && recipientPlayer) {
    return `${formatEntityName(creatorPlayer)} -> ${formatEntityName(recipientPlayer)}`;
  }
  if (creatorPlayer) {
    return formatEntityName(creatorPlayer);
  }
  if (recipientPlayer) {
    return formatEntityName(recipientPlayer);
  }

  return "";
};

const getMarkerTeamLabel = (marker = {}, match = {}) => {
  const markerTeamId = String(
    marker.creator_team_id ??
      marker.creator_team ??
      marker.team_id ??
      marker.possession_team_id ??
      ""
  );
  if (!markerTeamId) {
    return "";
  }

  const homeTeam = match.home_team;
  const awayTeam = match.away_team;
  const homeTeamId = String(homeTeam?.id ?? homeTeam?.team_id ?? "");
  const awayTeamId = String(awayTeam?.id ?? awayTeam?.team_id ?? "");

  if (homeTeamId && markerTeamId === homeTeamId) {
    return formatEntityName(homeTeam);
  }
  if (awayTeamId && markerTeamId === awayTeamId) {
    return formatEntityName(awayTeam);
  }

  return `Team #${markerTeamId}`;
};

const formatMarkerSecond = (second) => {
  if (second === undefined || second === null || second === "") {
    return "";
  }

  const numericSecond = Number(second);
  if (!Number.isFinite(numericSecond)) {
    return `${second}s`;
  }

  return `${Number.isInteger(numericSecond) ? numericSecond : numericSecond.toFixed(1)}s`;
};

const getResultExclusionKey = (matchKey, result = {}, index) =>
  `${matchKey}-${getSequenceId(result) || index}`;

const getSequenceId = (result = {}) =>
  result.sequence_id ||
  result.possession_sequence_id ||
  normalizeIdList(result.sequence_ids)?.[0] ||
  "";

const getStepTimeRange = (step = {}) => {
  const firstSecond = step.first_second ?? step.start_second ?? step.second;
  const lastSecond = step.last_second ?? step.end_second ?? firstSecond;

  if (firstSecond === undefined || firstSecond === null) {
    return "";
  }

  return firstSecond === lastSecond
    ? `${firstSecond}s`
    : `${firstSecond}s - ${lastSecond}s`;
};

const getStepTitle = (step = {}, requestedSteps = []) => {
  const requestedStep = requestedSteps[Number(step.index)];
  return (
    requestedStep?.sourceLabel ||
    step.kind ||
    step.action ||
    step.name ||
    `Step ${Number.isFinite(Number(step.index)) ? Number(step.index) + 1 : ""}`.trim()
  );
};

const buildOpenEventsTitle = ({ group, result, index }) => {
  const sequenceId = getSequenceId(result) || index + 1;
  return `${formatMatchTitle(group.match, "Sequence match")} | Sequence ${sequenceId}`;
};

// Ordered action names of one occurrence — the pattern's signature.
const getOccurrenceActions = (occurrence = {}) =>
  (Array.isArray(occurrence.markers) ? occurrence.markers : []).map(
    (marker) =>
      marker?.action ??
      marker?.parameter_name ??
      marker?.name ??
      `#${marker?.action_id ?? ""}`
  );

const buildPatternSignature = (occurrence) =>
  getOccurrenceActions(occurrence).join(" → ");

// Opponent Pattern Analysis: each result row is one occurrence. Occurrences in a
// match with the same ordered action signature are repetitions of one pattern;
// the row count is the repetition count.
const groupOccurrencesByPattern = (occurrences = []) => {
  const bySignature = new Map();
  occurrences.forEach((occurrence) => {
    const signature = buildPatternSignature(occurrence);
    if (!bySignature.has(signature)) {
      bySignature.set(signature, {
        signature,
        actions: getOccurrenceActions(occurrence),
        occurrences: [],
      });
    }
    bySignature.get(signature).occurrences.push(occurrence);
  });
  return Array.from(bySignature.values()).map((pattern) => ({
    ...pattern,
    count: pattern.occurrences.length,
  }));
};

const SearchableSelect = ({
  label,
  placeholder,
  endpoint,
  params,
  value,
  onChange,
  getLabel,
  getKey = getOptionId,
  dataTestId,
  noResultsLabel,
  clearLabel,
  transformOptions = identityOptions,
  enabled = true,
  minSearchLength = 0,
  openOnMount = false,
  apiClient = $apiV1,
  // When provided, options come from this preloaded list (filtered locally by
  // the typed query) instead of querying the endpoint.
  localOptions = null,
}) => {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(openOnMount);
  const [loading, setLoading] = useState(false);
  const paramsKey = JSON.stringify(params || {});
  const endpointsKey = JSON.stringify(
    Array.isArray(endpoint) ? endpoint : [endpoint]
  );
  const selectRef = useOutsideClick(() => {
    setOpen(false);
    setQuery("");
  });

  useEffect(() => {
    if (
      !enabled ||
      Array.isArray(localOptions) ||
      query.trim().length < minSearchLength
    ) {
      setOptions([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    const abortController = new AbortController();
    const requestParams = JSON.parse(paramsKey);
    const requestEndpoints = JSON.parse(endpointsKey);
    const timeoutId = window.setTimeout(async () => {
      setLoading(true);
      try {
        let response = null;
        let lastError = null;

        for (const requestEndpoint of requestEndpoints) {
          try {
            response = await apiClient.get(requestEndpoint, {
              params: {
                ...requestParams,
                search: query.trim(),
              },
              signal: abortController.signal,
            });
            break;
          } catch (error) {
            if (abortController.signal.aborted) {
              return;
            }
            lastError = error;
          }
        }

        if (!response) {
          throw lastError;
        }

        const nextOptions = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];
        if (!cancelled) {
          setOptions(transformOptions(nextOptions));
        }
      } catch (error) {
        if (!cancelled) {
          setOptions([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [
    apiClient,
    enabled,
    endpointsKey,
    localOptions,
    minSearchLength,
    paramsKey,
    query,
    transformOptions,
  ]);

  const selectedLabel = value ? getLabel(value) : "";
  const normalizedQuery = query.trim().toLowerCase();
  const displayedOptions = Array.isArray(localOptions)
    ? localOptions.filter((option) =>
        String(getLabel(option) || "")
          .toLowerCase()
          .includes(normalizedQuery)
      )
    : options;
  const shouldShowOptions =
    open && query.trim().length >= Math.max(0, minSearchLength);
  const handleQueryChange = (event) => {
    if (!enabled) {
      return;
    }
    setQuery(event.currentTarget.value);
    setOpen(true);
  };

  return (
    <FieldGroup>
      <Label>{label}</Label>
      <SearchBox ref={selectRef}>
        <TextInput
          data-testid={dataTestId}
          value={open ? query : selectedLabel}
          placeholder={placeholder}
          disabled={!enabled}
          onFocus={() => {
            if (!enabled) {
              return;
            }
            // Keep an active typeahead query intact when the input receives
            // focus again (for example after an options-list interaction).
            if (!open) {
              setQuery("");
            }
            setOpen(true);
          }}
          // onInput covers native typing immediately; onChange remains for
          // browsers and assistive technologies that dispatch only change.
          onInput={handleQueryChange}
          onChange={handleQueryChange}
        />
        {value && (
          <ClearButton
            type="button"
            aria-label={clearLabel}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              onChange(null);
              setQuery("");
            }}
          >
            x
          </ClearButton>
        )}
        {shouldShowOptions && (
          <OptionsList>
            {loading ? (
              <OptionItem $disabled>{placeholder}</OptionItem>
            ) : displayedOptions.length > 0 ? (
              displayedOptions.map((option, optionIndex) => (
                <OptionItem
                  // The upstream catalog can contain duplicate ids and labels.
                  // Keep every rendered row uniquely keyed so React removes
                  // non-matching rows instead of retaining stale DOM nodes.
                  key={`${getKey(option)}-${optionIndex}`}
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    onChange(option);
                    setQuery("");
                    setOpen(false);
                  }}
                >
                  {getLabel(option) || getOptionId(option)}
                </OptionItem>
              ))
            ) : (
              <OptionItem $disabled>{noResultsLabel}</OptionItem>
            )}
          </OptionsList>
        )}
      </SearchBox>
    </FieldGroup>
  );
};

const MAX_RECRUITMENT_SEQUENCES = 5;

const PlayerRecruitmentBuilder = ({
  sequences,
  eventOptions,
  onAddSequence,
  onDeleteSequence,
  onUpdateSequence,
  onAddEvent,
  onRemoveEvent,
}) => (
  <RecruitmentBuilderPanel>
    <RecruitmentStepEyebrow>
      <RecruitmentStepNumber>1</RecruitmentStepNumber>
      Step 1 of 3
      <span>{sequences.length} of {MAX_RECRUITMENT_SEQUENCES} sequences</span>
    </RecruitmentStepEyebrow>
    <RecruitmentPanelTitle>Build your Playing Style</RecruitmentPanelTitle>
    <RecruitmentPanelSubtitle>
      Create up to five event chains. Select the chains to include in this search.
    </RecruitmentPanelSubtitle>
    <RecruitmentCardRow>
      {sequences.map((sequence) => (
        <RecruitmentSequenceCard key={sequence.id}>
          <RecruitmentCardHead>
            <RecruitmentSequenceName
              value={sequence.name}
              onChange={(event) => onUpdateSequence(sequence.id, { name: event.target.value })}
              aria-label="Sequence name"
            />
            <RecruitmentIconButton type="button" onClick={() => onDeleteSequence(sequence.id)}>×</RecruitmentIconButton>
          </RecruitmentCardHead>
          <RecruitmentEventRow>
            {sequence.steps.map((step, index) => (
              <React.Fragment key={step.id}>
                {index > 0 && <RecruitmentArrow>→</RecruitmentArrow>}
                <RecruitmentEventChip type="button" onClick={() => onRemoveEvent(sequence.id, step.id)} title="Remove event">
                  {step.sourceLabel}<small>×</small>
                </RecruitmentEventChip>
              </React.Fragment>
            ))}
          </RecruitmentEventRow>
          <RecruitmentEventSelect
            value=""
            onChange={(event) => {
              const option = eventOptions.find((item) => String(item.id) === event.target.value);
              if (option) onAddEvent(sequence.id, option);
            }}
          >
            <option value="">+ Add Event</option>
            {eventOptions.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
          </RecruitmentEventSelect>
          <RecruitmentIncludeLabel>
            <input
              type="checkbox"
              checked={sequence.included}
              onChange={(event) => onUpdateSequence(sequence.id, { included: event.target.checked })}
            /> Include in search
          </RecruitmentIncludeLabel>
        </RecruitmentSequenceCard>
      ))}
      <RecruitmentAddCard type="button" disabled={sequences.length >= MAX_RECRUITMENT_SEQUENCES} onClick={onAddSequence}>
        <span>+</span> Add Sequence
      </RecruitmentAddCard>
    </RecruitmentCardRow>
  </RecruitmentBuilderPanel>
);

const SequenceSearch = () => {
  const { t } = useTranslation();
  const { notifications, addNotification, removeNotification } =
    useNotification();

  const [activeTab, setActiveTab] = useState(SEQUENCE_TAB_SEQUENCES);
  const [competition, setCompetition] = useState(null);
  const [season, setSeason] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [teamSeasonOptions, setTeamSeasonOptions] = useState([]);
  const [competitionSeasonOptions, setCompetitionSeasonOptions] = useState([]);
  // Teams that played the selected competition season. null = list unavailable
  // (no season picked / mapping failed) -> team picker falls back to the remote
  // search endpoint, which is not scoped to the competition.
  const [seasonTeamOptions, setSeasonTeamOptions] = useState(null);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [availableMatches, setAvailableMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState("");
  const [selectedMatchCount, setSelectedMatchCount] = useState(null);
  const [selectedMatches, setSelectedMatches] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [openMatchPresetDropdown, setOpenMatchPresetDropdown] = useState(false);
  const [openMatchesDropdown, setOpenMatchesDropdown] = useState(false);
  const [videoPlaybackMode, setVideoPlaybackMode] = useState(
    SEQUENCE_VIDEO_MODE_SEQUENCE
  );
  const [immediateEventsOnly, setImmediateEventsOnly] = useState(false);
  const [maxEventsBetween, setMaxEventsBetween] = useState("");
  // How many times the whole chain must occur within a single match for that
  // match to appear: "from" (min) and optional "to" (max). Empty = no bound.
  const [minSequenceCountPerMatch, setMinSequenceCountPerMatch] = useState("");
  const [maxSequenceCountPerMatch, setMaxSequenceCountPerMatch] = useState("");
  const [steps, setSteps] = useState([]);
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [resultsPageLoading, setResultsPageLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [recruitmentResponse, setRecruitmentResponse] = useState(null);
  const [openRecruitmentPlayers, setOpenRecruitmentPlayers] = useState({});
  const [recruitmentSequences, setRecruitmentSequences] = useState([]);
  const [submitError, setSubmitError] = useState("");
  const [resultsPage, setResultsPage] = useState(1);
  const [excludedResultMarkers, setExcludedResultMarkers] = useState({});
  // Results display only: which sequence chip is active per match, and which
  // marker lists are expanded. Does not affect search, payload, or video.
  const [activeSequenceByMatch, setActiveSequenceByMatch] = useState({});
  const [openMarkerLists, setOpenMarkerLists] = useState({});
  // Snapshot of `immediateEventsOnly` at the moment the displayed results were
  // fetched. Tells the sequence video to join only the selected markers into one
  // clip, even if the form toggle changes after the search.
  const [appliedImmediateEventsOnly, setAppliedImmediateEventsOnly] =
    useState(false);
  // Snapshot of the steps submitted with the displayed results. Drives the
  // per-result step list so every chosen event is shown in order — including
  // excluded ones, which the API leaves out of `matched_steps`.
  const [appliedSteps, setAppliedSteps] = useState([]);
  // True when the displayed results came from an Opponent Pattern Analysis search,
  // which groups occurrences into repeated patterns instead of the step view.
  const [appliedPatternAnalysis, setAppliedPatternAnalysis] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [tutorialRect, setTutorialRect] = useState(null);
  const tutorialLaunchResolvedRef = useRef(false);
  const tutorialTabsRef = useRef(null);
  const tutorialScopeRef = useRef(null);
  const tutorialMatchesRef = useRef(null);
  const tutorialVideoModeRef = useRef(null);
  const tutorialTimelineRef = useRef(null);
  const tutorialTemplatesRef = useRef(null);
  const playerRecruitmentSequencesRef = useRef(null);
  const tutorialResultsRef = useRef(null);
  const activeSearchPayloadRef = useRef(null);
  const [playerRecruitmentTemplateFilters, setPlayerRecruitmentTemplateFilters] =
    useState({});
  // Event-picker popover, opened from the "Add step" card; closes on outside click.
  const [eventPickerOpen, setEventPickerOpen] = useState(false);
  const eventPickerRef = useOutsideClick(() => setEventPickerOpen(false));
  // The event catalog is small and effectively static. Load it with the page so
  // opening Add step never waits for a network request.
  const [sequenceEventOptions, setSequenceEventOptions] = useState([]);
  // "Load example" preset menu.
  const [exampleMenuOpen, setExampleMenuOpen] = useState(false);
  const exampleMenuRef = useOutsideClick(() => setExampleMenuOpen(false));
  // Caches the merged match pool per competition|season|team-set so re-selecting
  // a scope (notably the season-wide, no-team pool) does not refetch.
  const matchPoolCacheRef = useRef({});
  const sequenceTutorialSteps = useMemo(() => buildSequenceTutorialSteps(t), [t]);
  const sequenceTutorialDemoSteps = useMemo(
    () => buildSequenceTutorialDemoSteps(),
    []
  );
  const shouldShowTutorialDemoSteps = showTutorial && steps.length === 0;
  const displayedSteps = shouldShowTutorialDemoSteps
    ? sequenceTutorialDemoSteps
    : steps;

  const sequenceEventOptionsTransform = useCallback(
    (options) => buildSequenceEventOptionsFromApiMarkers(options),
    []
  );

  useEffect(() => {
    let cancelled = false;

    const loadSequenceEventOptions = async () => {
      try {
        const response = await $apiV2.get("/stats/marker_actions", {
          params: { page: 1, page_size: 250, events_only: 1 },
        });
        const options = Array.isArray(response.data?.results)
          ? response.data.results
          : Array.isArray(response.data)
            ? response.data
            : [];

        if (!cancelled) {
          setSequenceEventOptions(sequenceEventOptionsTransform(options));
        }
      } catch (error) {
        if (!cancelled) {
          setSequenceEventOptions([]);
        }
      }
    };

    loadSequenceEventOptions();

    return () => {
      cancelled = true;
    };
  }, [sequenceEventOptionsTransform]);

  const displayedResponse =
    showTutorial && !response ? SEQUENCE_TUTORIAL_DEMO_RESPONSE : response;
  const isTutorialDemoResponse = showTutorial && !response;
  const sequenceResults = useMemo(
    () => displayedResponse?.results || [],
    [displayedResponse]
  );
  const allGroupedResults = useMemo(
    () => groupSequenceResultsByMatch(sequenceResults),
    [sequenceResults]
  );
  // The backend exposes the total sequence count, while the match count is
  // derived from the matches represented on the currently loaded page.
  const totalMatchesFound = allGroupedResults.length;
  const totalSequencesFound = useMemo(() => {
    // Prefer the server's total count; the loaded results may be deduped or
    // (for the tutorial demo) lack a count, so fall back to summing groups.
    const serverCount = Number(displayedResponse?.count);
    if (Number.isFinite(serverCount) && serverCount > 0) {
      return serverCount;
    }
    return allGroupedResults.reduce(
      (sum, group) => sum + (group.sequence_ids?.length || 0),
      0
    );
  }, [allGroupedResults, displayedResponse]);
  const totalResultPages = Math.max(
    1,
    Math.ceil(
      (isTutorialDemoResponse
        ? sequenceResults.length
        : Number(displayedResponse?.count) || sequenceResults.length) /
        (isTutorialDemoResponse
          ? RESULTS_PAGE_SIZE
          : Number(displayedResponse?.page_size) || RESULTS_PAGE_SIZE)
    )
  );
  const paginatedSequenceResults = useMemo(() => {
    if (!isTutorialDemoResponse) {
      return sequenceResults;
    }

    const startIndex = (resultsPage - 1) * RESULTS_PAGE_SIZE;
    return sequenceResults.slice(startIndex, startIndex + RESULTS_PAGE_SIZE);
  }, [isTutorialDemoResponse, resultsPage, sequenceResults]);
  const groupedResults = useMemo(
    () => groupSequenceResultsByMatch(paginatedSequenceResults),
    [paginatedSequenceResults]
  );
  // Opponent Pattern Analysis view: group the current page's occurrences by
  // match, then by pattern signature.
  const opponentMatchGroups = useMemo(() => {
    if (!appliedPatternAnalysis) {
      return [];
    }
    return groupSequenceResultsByMatch(sequenceResults).map((group) => ({
      ...group,
      patterns: groupOccurrencesByPattern(group.results),
    }));
  }, [appliedPatternAnalysis, sequenceResults]);
  const opponentTotalPages = Math.max(
    1,
    Math.ceil(
      (isTutorialDemoResponse
        ? opponentMatchGroups.length
        : Number(displayedResponse?.count) || opponentMatchGroups.length) /
        (isTutorialDemoResponse
          ? RESULTS_PAGE_SIZE
          : Number(displayedResponse?.page_size) || RESULTS_PAGE_SIZE)
    )
  );
  const paginatedOpponentMatchGroups = useMemo(() => {
    if (!isTutorialDemoResponse) {
      return opponentMatchGroups;
    }

    const startIndex = (resultsPage - 1) * RESULTS_PAGE_SIZE;
    return opponentMatchGroups.slice(startIndex, startIndex + RESULTS_PAGE_SIZE);
  }, [isTutorialDemoResponse, opponentMatchGroups, resultsPage]);
  const effectiveTotalPages = appliedPatternAnalysis
    ? opponentTotalPages
    : totalResultPages;
  const defaultStepTeamLink = "selected_team";
  const seasonOptionsTransform = useCallback(
    (options) => {
      if (teamSeasonOptions.length > 0) {
        return filterAvailableSeasonOptions(teamSeasonOptions);
      }
      if (competitionSeasonOptions.length > 0) {
        return filterAvailableSeasonOptions(competitionSeasonOptions);
      }
      return filterAvailableSeasonOptions(options);
    },
    [competitionSeasonOptions, teamSeasonOptions]
  );
  const selectedCompetitionId = getOptionId(competition);
  const selectedSeasonId = getSeasonId(season);
  const selectedTeamIds = useMemo(
    () => selectedTeams.map((teamItem) => getOptionId(teamItem)).filter(Boolean),
    [selectedTeams]
  );
  // Match selection (both Pre-set and the individual Matches list) needs only
  // competition + season — a team is optional. Without a team the pool is the
  // whole season (every season team's matches, merged); with teams it is scoped
  // to them.
  const matchScopeReady = Boolean(selectedCompetitionId && selectedSeasonId);
  const seasonTeamIds = useMemo(
    () =>
      (Array.isArray(seasonTeamOptions) ? seasonTeamOptions : [])
        .map((teamOption) => getOptionId(teamOption))
        .filter(Boolean),
    [seasonTeamOptions]
  );
  const matchPoolTeamIds = useMemo(
    () => (selectedTeamIds.length > 0 ? selectedTeamIds : seasonTeamIds),
    [selectedTeamIds, seasonTeamIds]
  );
  // Identifies the pool source; also the cache key. Empty until a pool can load.
  const matchPoolKey =
    matchScopeReady && matchPoolTeamIds.length > 0
      ? `${selectedCompetitionId}|${selectedSeasonId}|${[...matchPoolTeamIds]
          .sort()
          .join(",")}`
      : "";
  // Presets/derivation run once a pool can be loaded (scope chosen + at least one
  // team to query, whether selected or season-wide).
  const matchControlsEnabled = Boolean(matchPoolKey);
  // One-line summary of the active scope, shown beside the search action.
  const scopeSummaryText = useMemo(() => {
    if (!matchScopeReady) {
      return "";
    }
    const teamsLabel = selectedTeams.length
      ? selectedTeams.length > 1
        ? `${getTeamLabel(selectedTeams[0])} +${selectedTeams.length - 1}`
        : getTeamLabel(selectedTeams[0])
      : t("sequenceSearch.allTeams");
    const parts = [teamsLabel, getSeasonLabel(season)];
    if (selectedMatchCount?.title) {
      parts.push(selectedMatchCount.title);
    }
    return `${parts.filter(Boolean).join(" · ")} (${selectedMatches.length} ${t(
      "sequenceSearch.matches"
    ).toLowerCase()})`;
  }, [
    matchScopeReady,
    season,
    selectedMatchCount,
    selectedMatches.length,
    selectedTeams,
    t,
  ]);
  const allMatchesLabel = t("sequenceSearch.allMatches");
  const lastMatchLabel = t("sequenceSearch.lastMatch");
  const last3MatchesLabel = t("sequenceSearch.last3Matches");
  const last5MatchesLabel = t("sequenceSearch.last5Matches");
  const last10MatchesLabel = t("sequenceSearch.last10Matches");
  const customMatchesLabel = t("sequenceSearch.customMatches");
  const loadingMatchesLabel = t("sequenceSearch.loadingMatches");
  const selectMatchesLabel = t("sequenceSearch.selectMatches");
  const matchPresetPlaceholderLabel = t("sequenceSearch.matchPresetPlaceholder");
  const matchesLoadErrorLabel = t("sequenceSearch.matchesLoadError");
  const matchPresetDropdownRef = useOutsideClick(() => {
    if (openMatchPresetDropdown) {
      setOpenMatchPresetDropdown(false);
    }
  });
  const matchesDropdownRef = useOutsideClick(() => {
    if (openMatchesDropdown) {
      setOpenMatchesDropdown(false);
    }
  });
  const matchPresetLabel = useCallback(
    (key) =>
      ({
        1066: allMatchesLabel,
        1067: last3MatchesLabel,
        1068: last5MatchesLabel,
        1069: last10MatchesLabel,
        1688: lastMatchLabel,
      }[key] || ""),
    [
      allMatchesLabel,
      last3MatchesLabel,
      last5MatchesLabel,
      last10MatchesLabel,
      lastMatchLabel,
    ]
  );
  const competitionScopedMatches = useMemo(
    () =>
      (availableMatches || []).filter((match) =>
        selectedCompetitionId
          ? Number(match?.competition_id) === Number(selectedCompetitionId)
          : true
      ),
    [availableMatches, selectedCompetitionId]
  );
  const dateFilteredMatches = useMemo(() => {
    if (!dateRange) {
      return competitionScopedMatches;
    }

    return filterMatchesByDateRange({
      matches: competitionScopedMatches,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
    });
  }, [competitionScopedMatches, dateRange]);
  const matchPresetOptions = useMemo(
    () => createMatchesCountOptions(dateFilteredMatches.length, matchPresetLabel),
    [dateFilteredMatches.length, matchPresetLabel]
  );
  const selectedMatchIds = useMemo(
    () => getSelectedMatchIds(selectedMatches),
    [selectedMatches]
  );
  const selectedMatchIdSet = useMemo(
    () => new Set(selectedMatchIds.map((matchId) => String(matchId))),
    [selectedMatchIds]
  );
  const selectedMatchesLabel = useMemo(() => {
    if (matchesLoading) {
      return loadingMatchesLabel;
    }
    if (selectedMatches.length === 0) {
      return selectMatchesLabel;
    }

    const labels = selectedMatches.map((match) => formatMatchSelectLabel(match));
    return labels.length > 1
      ? `${labels[0]} + ${labels.length - 1}`
      : labels[0];
  }, [loadingMatchesLabel, matchesLoading, selectMatchesLabel, selectedMatches]);
  const selectedMatchPresetLabel =
    selectedMatchCount?.title || matchPresetPlaceholderLabel;
  const isSequenceVideoMode =
    videoPlaybackMode === SEQUENCE_VIDEO_MODE_SEQUENCE;
  const videoActionLabel = isSequenceVideoMode
    ? t("sequenceSearch.openSequenceVideo")
    : t("sequenceSearch.openSeparateVideos");
  // Per-tab form composition. Opponent Pattern Analysis has no step timeline
  // (so no immediate-events / max-events options) but is the only tab carrying
  // chain-repetition bounds. Video playback mode shows on every tab.
  const isRecruitmentTab = activeTab === SEQUENCE_TAB_PLAYER_RECRUITMENT;
  const tabHasTimeline = activeTab !== SEQUENCE_TAB_OPPONENT;
  const tabHasChainRepetitions = activeTab === SEQUENCE_TAB_OPPONENT;
  const tabHasPlayerSearch = activeTab === SEQUENCE_TAB_SEQUENCES || isRecruitmentTab;
  // Opponent Pattern Analysis runs the backend's auto-detect mode.
  const isPatternAnalysisTab = activeTab === SEQUENCE_TAB_OPPONENT;
  // Submit only the inputs the active tab exposes, so values typed on one tab
  // never leak into another tab's query. Opponent Pattern Analysis therefore
  // sends no steps (empty) plus its chain-repetition bounds; the timeline tabs
  // send steps but no chain-repetition bounds.
  const submittedSteps = tabHasTimeline ? steps : [];
  const submittedMaxEventsBetween = tabHasTimeline ? maxEventsBetween : "";
  const submittedImmediateEventsOnly = isRecruitmentTab
    ? true
    : tabHasTimeline
    ? immediateEventsOnly
    : false;
  const submittedMinSequenceCount = tabHasChainRepetitions
    ? minSequenceCountPerMatch
    : "";
  const submittedMaxSequenceCount = tabHasChainRepetitions
    ? maxSequenceCountPerMatch
    : "";

  useEffect(() => {
    if (resultsPage > effectiveTotalPages) {
      setResultsPage(effectiveTotalPages);
    }
  }, [resultsPage, effectiveTotalPages]);

  useEffect(() => {
    if (eventPickerOpen) {
      eventPickerRef.current?.querySelector("input")?.focus();
    }
  }, [eventPickerOpen, eventPickerRef]);

  useEffect(() => {
    const competitionId = getOptionId(competition);

    if (!competitionId) {
      setCompetitionSeasonOptions([]);
      return;
    }

    let cancelled = false;

    const loadCompetitionSeasons = async () => {
      try {
        const result = await $apiV1.get(`/platform/year_season`, {
          params: { competitions_id: competitionId, per_page: 100 },
        });
        if (!cancelled) {
          const seasons = Array.isArray(result.data?.results)
            ? result.data.results
            : Array.isArray(result.data)
              ? result.data
              : [];
          setCompetitionSeasonOptions(seasons);
        }
      } catch (error) {
        if (!cancelled) {
          setCompetitionSeasonOptions([]);
        }
      }
    };

    loadCompetitionSeasons();

    return () => {
      cancelled = true;
    };
  }, [competition]);

  useEffect(() => {
    const yearSeasonId = getSeasonId(season);
    const competitionSeasons = Array.isArray(competition?.seasons)
      ? competition.seasons
      : [];

    if (!yearSeasonId || competitionSeasons.length === 0) {
      setSeasonTeamOptions(null);
      return undefined;
    }

    const matchedSeason = competitionSeasons.find((entry) => {
      const entryYearSeasonId = entry?.year_season?.id ?? entry?.year_season_id;
      return String(entryYearSeasonId) === String(yearSeasonId);
    });

    if (!matchedSeason?.id) {
      setSeasonTeamOptions(null);
      return undefined;
    }

    let cancelled = false;

    const loadSeasonTeams = async () => {
      try {
        const result = await $apiV1.get(`/platform/season/${matchedSeason.id}`);
        if (cancelled) {
          return;
        }
        const teams = Array.isArray(result.data?.teams)
          ? result.data.teams
          : [];
        setSeasonTeamOptions(
          [...teams].sort((left, right) =>
            String(getTeamLabel(left) || "").localeCompare(
              String(getTeamLabel(right) || "")
            )
          )
        );
      } catch (error) {
        if (!cancelled) {
          setSeasonTeamOptions(null);
        }
      }
    };

    loadSeasonTeams();

    return () => {
      cancelled = true;
    };
  }, [competition, season]);

  useEffect(() => {
    if (selectedTeamIds.length === 0) {
      setTeamSeasonOptions([]);
      return undefined;
    }

    let cancelled = false;
    const competitionId = getOptionId(competition);

    const loadTeamSeasons = async () => {
      try {
        const seasonLists = await Promise.all(
          selectedTeamIds.map((teamId) =>
            $apiV1
              .get(`/statistics/team_opt/${teamId}`)
              .then((result) =>
                buildTeamSeasonOptions(result.data, competitionId)
              )
              .catch(() => [])
          )
        );
        if (cancelled) {
          return;
        }
        // Union every selected team's available seasons, deduped by season id.
        const mergedById = new Map();
        seasonLists.flat().forEach((seasonOption) => {
          const seasonId = String(getSeasonId(seasonOption) ?? "");
          if (seasonId && !mergedById.has(seasonId)) {
            mergedById.set(seasonId, seasonOption);
          }
        });
        setTeamSeasonOptions(Array.from(mergedById.values()));
      } catch (error) {
        if (!cancelled) {
          setTeamSeasonOptions([]);
        }
      }
    };

    loadTeamSeasons();

    return () => {
      cancelled = true;
    };
  }, [competition, selectedTeamIds]);

  useEffect(() => {
    if (!matchPoolKey) {
      return undefined;
    }

    let cancelled = false;
    const teamIdsForPool = matchPoolTeamIds;
    const seasonId = selectedSeasonId;

    const loadMatches = async () => {
      setMatchesLoading(true);
      setMatchesError("");
      setSelectedMatchCount(null);
      setSelectedMatches([]);
      setDateRange(null);

      // Cached pools (notably the heavier season-wide, no-team one) skip refetch.
      const cached = matchPoolCacheRef.current[matchPoolKey];
      if (cached) {
        if (!cancelled) {
          setAvailableMatches(cached);
          setMatchesLoading(false);
        }
        return;
      }

      try {
        // matches_opt is a per-team endpoint. With teams selected we query them;
        // with none we query every season team and merge into the whole-season
        // pool. Either way: dedupe by match id, sort newest first.
        const settled = await Promise.allSettled(
          teamIdsForPool.map((teamId) =>
            $apiV2.get(
              `/platform/team/${teamId}/matches_opt?year_season_id=${seasonId}&params_ids=`
            )
          )
        );

        const fulfilled = settled.filter(
          (outcome) => outcome.status === "fulfilled"
        );
        if (fulfilled.length === 0) {
          throw settled[0]?.reason;
        }

        const mergedById = new Map();
        fulfilled
          .flatMap((outcome) =>
            Array.isArray(outcome.value?.data) ? outcome.value.data : []
          )
          .map(normalizeAvailableMatch)
          .filter((match) => match.id)
          .forEach((match) => {
            const key = String(match.id);
            if (!mergedById.has(key)) {
              mergedById.set(key, match);
            }
          });

        const nextMatches = Array.from(mergedById.values()).sort(
          (left, right) => new Date(right?.date) - new Date(left?.date)
        );

        if (!cancelled) {
          matchPoolCacheRef.current[matchPoolKey] = nextMatches;
          setAvailableMatches(nextMatches);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailableMatches([]);
          setMatchesError(
            error?.response?.data?.message || matchesLoadErrorLabel
          );
        }
      } finally {
        if (!cancelled) {
          setMatchesLoading(false);
        }
      }
    };

    loadMatches();

    return () => {
      cancelled = true;
    };
  }, [matchPoolKey, matchPoolTeamIds, selectedSeasonId, matchesLoadErrorLabel]);

  useEffect(() => {
    if (!matchControlsEnabled || matchesLoading) {
      return;
    }
    if (matchPresetOptions.length === 0 || dateFilteredMatches.length === 0) {
      setSelectedMatchCount(null);
      setSelectedMatches([]);
      return;
    }

    const currentPreset = matchPresetOptions.find(
      (option) => option.id === selectedMatchCount?.id
    );

    if (!selectedMatchCount) {
      setSelectedMatchCount(matchPresetOptions[0]);
      return;
    }
    if (selectedMatchCount.id === CUSTOM_MATCH_PRESET_ID) {
      return;
    }
    if (!currentPreset) {
      setSelectedMatchCount(matchPresetOptions[0]);
      return;
    }
    if (
      currentPreset.value !== selectedMatchCount.value ||
      currentPreset.title !== selectedMatchCount.title
    ) {
      setSelectedMatchCount(currentPreset);
    }
  }, [
    dateFilteredMatches.length,
    matchControlsEnabled,
    matchPresetOptions,
    matchesLoading,
    selectedMatchCount,
  ]);

  useEffect(() => {
    if (!matchControlsEnabled || !selectedMatchCount) {
      return;
    }

    if (selectedMatchCount.id === CUSTOM_MATCH_PRESET_ID) {
      setSelectedMatches((previousMatches) => {
        const availableIds = new Set(
          dateFilteredMatches.map((match) => String(match.id))
        );
        return previousMatches.filter((match) =>
          availableIds.has(String(match.id))
        );
      });
      return;
    }

    setSelectedMatches(
      deriveSelectedMatches({
        matches: dateFilteredMatches,
        selectedCompetition: selectedCompetitionId
          ? [{ competition_id: selectedCompetitionId }]
          : [],
        selectedMatchCount,
      })
    );
  }, [
    dateFilteredMatches,
    matchControlsEnabled,
    selectedCompetitionId,
    selectedMatchCount,
  ]);

  const updateStep = (stepId, patch) => {
    setSteps((previousSteps) =>
      previousSteps.map((step) =>
        step.id === stepId ? { ...step, ...patch } : step
      )
    );
  };

  const resetMatchFilters = () => {
    setAvailableMatches([]);
    setMatchesLoading(false);
    setMatchesError("");
    setSelectedMatchCount(null);
    setSelectedMatches([]);
    setDateRange(null);
    setOpenMatchPresetDropdown(false);
    setOpenMatchesDropdown(false);
  };

  // Full reset back to the page's initial state: scope, query-builder inputs and
  // any displayed results. Used when switching top-level tabs so each tab always
  // starts blank instead of inheriting the previous tab's selections.
  const resetForm = () => {
    // Scope
    setCompetition(null);
    setSeason(null);
    setSelectedTeams([]);
    setSelectedPlayers([]);
    setSeasonTeamOptions(null);
    resetMatchFilters();
    // Query-builder inputs
    setSteps([]);
    setDraggedStepIndex(null);
    setMaxEventsBetween("");
    setImmediateEventsOnly(false);
    setMinSequenceCountPerMatch("");
    setMaxSequenceCountPerMatch("");
    setVideoPlaybackMode(SEQUENCE_VIDEO_MODE_SEQUENCE);
    // Results + their applied snapshots
    setResponse(null);
    setRecruitmentResponse(null);
    setOpenRecruitmentPlayers({});
    setRecruitmentSequences([]);
    setSubmitError("");
    setSubmitting(false);
    setResultsPageLoading(false);
    setResultsPage(1);
    activeSearchPayloadRef.current = null;
    setExcludedResultMarkers({});
    setActiveSequenceByMatch({});
    setOpenMarkerLists({});
    setAppliedImmediateEventsOnly(false);
    setAppliedSteps([]);
    setAppliedPatternAnalysis(false);
    // Transient popovers
    setEventPickerOpen(false);
    setExampleMenuOpen(false);
  };

  const handleTabChange = (tabId) => {
    if (tabId === activeTab) {
      return;
    }
    resetForm();
    setActiveTab(tabId);
  };

  const handleAddTeam = (option) => {
    if (!option) {
      return;
    }

    setSelectedTeams((previousTeams) => {
      const teamId = String(getOptionId(option) ?? "");
      if (
        !teamId ||
        previousTeams.some((item) => String(getOptionId(item)) === teamId)
      ) {
        return previousTeams;
      }
      return [...previousTeams, option];
    });
    // The merged match list depends on the selected teams, so reset the match
    // selection whenever the set changes.
    resetMatchFilters();
  };

  const handleRemoveTeam = (option) => {
    const teamId = String(getOptionId(option) ?? "");
    setSelectedTeams((previousTeams) =>
      previousTeams.filter((item) => String(getOptionId(item)) !== teamId)
    );
    resetMatchFilters();
  };

  const handleSeasonChange = (option) => {
    setSeason(option);
    setSelectedTeams([]);
    setSelectedPlayers([]);
    setTeamSeasonOptions([]);
    resetMatchFilters();
  };

  const handleCompetitionChange = (option) => {
    setCompetition(option);
    setSeason(null);
    setSelectedTeams([]);
    setSelectedPlayers([]);
    setTeamSeasonOptions([]);
    resetMatchFilters();
  };

  const templateFilters = buildSequenceTemplateFilters({
    competition,
    season,
    teams: selectedTeams,
    selectedPlayers,
    videoPlaybackMode,
    immediateEventsOnly,
    maxEventsBetween,
    steps,
  });

  const applyTemplateFilters = (filters) => {
    const parsed = parseSequenceTemplateFilters(filters, createStepId);

    setCompetition(parsed.competition);
    setSeason(parsed.season);
    setSelectedTeams(parsed.teams);
    setSelectedPlayers(parsed.selectedPlayers);
    setVideoPlaybackMode(parsed.videoPlaybackMode);
    setImmediateEventsOnly(parsed.immediateEventsOnly);
    setMaxEventsBetween(parsed.maxEventsBetween);
    setSteps(parsed.steps);
    setTeamSeasonOptions([]);
    resetMatchFilters();
    setSubmitError("");
  };

  const savedTemplateControls = useSavedFilterTemplates({
    pageKey: SEQUENCE_TEMPLATE_PAGE_KEY,
    filters: templateFilters,
    onApplyTemplate: applyTemplateFilters,
    onSaveSuccess: (savedTemplate) => {
      addNotification(
        t("smrtSearch.templateSavedToast", {
          name: savedTemplate?.title || "",
        }),
        "success"
      );
    },
  });

  const playerRecruitmentTemplateControls = useSavedFilterTemplates({
    pageKey: PLAYER_RECRUITMENT_TEMPLATE_PAGE_KEY,
    filters: playerRecruitmentTemplateFilters,
    onApplyTemplate: (filters) => {
      playerRecruitmentSequencesRef.current?.applyTemplate(filters);
    },
    onSaveSuccess: (savedTemplate) => {
      addNotification(
        t("smrtSearch.templateSavedToast", {
          name: savedTemplate?.title || "",
        }),
        "success"
      );
    },
  });

  // Block the save/update modals when the form is empty — an all-null template
  // would restore nothing.
  const guardTemplateModalOpen = (openModal) => () => {
    if (!hasSequenceTemplateContent(templateFilters)) {
      savedTemplateControls.setStatusMessage({
        type: "error",
        text: t("savedTemplates.filtersRequired"),
      });
      return;
    }

    savedTemplateControls.setStatusMessage(null);
    openModal();
  };

  const guardPlayerRecruitmentTemplateModalOpen = (openModal) => () => {
    if (!hasPlayerRecruitmentTemplateContent(playerRecruitmentTemplateFilters)) {
      playerRecruitmentTemplateControls.setStatusMessage({
        type: "error",
        text: t("savedTemplates.filtersRequired"),
      });
      return;
    }

    playerRecruitmentTemplateControls.setStatusMessage(null);
    openModal();
  };

  const handleAddPlayer = (option) => {
    if (!option) {
      return;
    }

    setSelectedPlayers((previousPlayers) => {
      const playerId = String(getOptionId(option) ?? "");
      if (
        !playerId ||
        previousPlayers.some(
          (item) => String(getOptionId(item)) === playerId
        )
      ) {
        return previousPlayers;
      }
      return [...previousPlayers, option];
    });
  };

  const handleRemovePlayer = (option) => {
    const playerId = String(getOptionId(option) ?? "");
    setSelectedPlayers((previousPlayers) =>
      previousPlayers.filter(
        (item) => String(getOptionId(item)) !== playerId
      )
    );
  };

  const handleDateRangeChange = useCallback((startDate, endDate) => {
    setDateRange({ startDate, endDate });
  }, []);

  const handleMatchToggle = useCallback(
    (match) => {
      setSelectedMatchCount({
        id: CUSTOM_MATCH_PRESET_ID,
        value: selectedMatches.length,
        title: customMatchesLabel,
      });
      setSelectedMatches((previousMatches) => {
        const nextIds = new Set(previousMatches.map((item) => String(item.id)));
        const currentId = String(match?.id);

        if (nextIds.has(currentId)) {
          nextIds.delete(currentId);
        } else {
          nextIds.add(currentId);
        }

        return dateFilteredMatches.filter((item) =>
          nextIds.has(String(item.id))
        );
      });
    },
    [customMatchesLabel, dateFilteredMatches, selectedMatches.length]
  );

  const handleDeselectAllMatches = useCallback(() => {
    setSelectedMatchCount({
      id: CUSTOM_MATCH_PRESET_ID,
      value: 0,
      title: customMatchesLabel,
    });
    setSelectedMatches([]);
  }, [customMatchesLabel]);

  const removeStep = (stepId) => {
    setSteps((previousSteps) =>
      previousSteps.filter((step) => step.id !== stepId)
    );
  };

  const handleAddEventOption = (option) => {
    setSteps((previousSteps) => [
      ...previousSteps,
      createSequenceStepFromEventOption(option, createStepId(), defaultStepTeamLink),
    ]);
  };

  const handleLoadExample = (preset) => {
    const exampleSteps = buildExampleSequenceSteps(preset);
    if (exampleSteps.length > 0) {
      setSteps(exampleSteps);
    }
  };

  // Clear the timeline back to empty, which also brings the "Load example" menu
  // back so a different example can be loaded.
  const handleResetSteps = () => {
    setSteps([]);
    setDraggedStepIndex(null);
  };

  const handleOpenSequenceVideo = (
    markers,
    title,
    allMarkers = markers,
    selectedOnly = false
  ) => {
    if (markers.length === 0) {
      return;
    }

    // `markers` is the kept set; pass the full ordered set as allMarkers so the
    // merged player can break the sequence at the markers the user excluded.
    const fullMarkers =
      Array.isArray(allMarkers) && allMarkers.length > 0 ? allMarkers : markers;

    // selectedOnly (immediate-events results): join only the selected markers
    // into one merged clip instead of bridging every marker in the sequence.
    openVideoList(markers.join(","), title, true, fullMarkers, true, selectedOnly);
  };

  const handleOpenSeparateVideos = (markers, title) => {
    if (markers.length === 0) {
      return;
    }

    openVideoList(markers.join(","), title, true, markers);
  };

  const handleOpenResultVideo = ({
    markers,
    title,
    allMarkers = markers,
    sequenceTitle,
  }) => {
    if (isSequenceVideoMode) {
      handleOpenSequenceVideo(
        markers,
        sequenceTitle || `${title} - ${t("sequenceSearch.openSequenceVideo")}`,
        allMarkers,
        appliedImmediateEventsOnly
      );
      return;
    }

    handleOpenSeparateVideos(markers, title);
  };

  const selectedPlayerIds = selectedPlayers
    .map((item) => getOptionId(item))
    .filter(Boolean);

  const validRecruitmentSequences = useMemo(
    () => recruitmentSequences.filter((sequence) => sequence.included && sequence.steps.length > 0),
    [recruitmentSequences]
  );

  const addRecruitmentSequence = () => {
    setRecruitmentSequences((current) => {
      if (current.length >= MAX_RECRUITMENT_SEQUENCES) return current;
      const usedNames = new Set(current.map((sequence) => sequence.name));
      let number = 1;
      while (usedNames.has(`Sequence ${number}`)) number++;
      return [...current, { id: createStepId(), name: `Sequence ${number}`, steps: [], included: false }];
    });
  };

  const updateRecruitmentSequence = (sequenceId, patch) =>
    setRecruitmentSequences((current) => current.map((sequence) =>
      sequence.id === sequenceId ? { ...sequence, ...patch } : sequence
    ));

  const addRecruitmentEvent = (sequenceId, option) =>
    setRecruitmentSequences((current) => current.map((sequence) =>
      sequence.id === sequenceId
        ? { ...sequence, steps: [...sequence.steps, createSequenceStepFromEventOption(option, createStepId(), "selected_team")] }
        : sequence
    ));

  const removeRecruitmentEvent = (sequenceId, stepId) =>
    setRecruitmentSequences((current) => current.map((sequence) =>
      sequence.id === sequenceId
        ? { ...sequence, steps: sequence.steps.filter((step) => step.id !== stepId) }
        : sequence
    ));

  const validateForm = () => {
    if (isRecruitmentTab) {
      if (!getOptionId(competition)) return t("sequenceSearch.competitionRequired");
      if (!getSeasonId(season)) return t("sequenceSearch.seasonRequired");
      if (validRecruitmentSequences.length === 0) return t("sequenceSearch.recruitmentSequencesRequired");
      return "";
    }
    const payload = buildSequenceSearchPayload({
      competitionId: getOptionId(competition),
      seasonId: getSeasonId(season),
      matchIds: selectedMatchIds,
      teamIds: selectedTeamIds,
      playerIds: selectedPlayerIds,
      maxEventsBetween: submittedMaxEventsBetween,
      minSequenceCountPerMatch: submittedMinSequenceCount,
      maxSequenceCountPerMatch: submittedMaxSequenceCount,
      steps: submittedSteps,
      sequenceView: isSequenceVideoMode,
      immediateEventsOnly: submittedImmediateEventsOnly,
      patternAnalysis: isPatternAnalysisTab,
    });

    if (!payload.competition_id) {
      return t("sequenceSearch.competitionRequired");
    }
    if (!payload.season_id) {
      return t("sequenceSearch.seasonRequired");
    }
    // Opponent Pattern Analysis has no step timeline, so the chain of steps is
    // not a requirement there.
    if (tabHasTimeline && payload.steps.length === 0) {
      return t("sequenceSearch.stepsRequired");
    }

    return "";
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitError("");

    const validationMessage = validateForm();
    if (validationMessage) {
      setSubmitError(validationMessage);
      addNotification(validationMessage, "error");
      return;
    }

    const payload = buildSequenceSearchPayload({
      competitionId: getOptionId(competition),
      seasonId: getSeasonId(season),
      matchIds: selectedMatchIds,
      teamIds: selectedTeamIds,
      playerIds: selectedPlayerIds,
      maxEventsBetween: submittedMaxEventsBetween,
      minSequenceCountPerMatch: submittedMinSequenceCount,
      maxSequenceCountPerMatch: submittedMaxSequenceCount,
      steps: submittedSteps,
      sequenceView: isSequenceVideoMode,
      immediateEventsOnly: submittedImmediateEventsOnly,
      patternAnalysis: isPatternAnalysisTab,
    });

    if (isRecruitmentTab) {
      payload.sequences = validRecruitmentSequences.map((sequence) => ({
        steps: buildSequenceSearchPayload({
          competitionId: getOptionId(competition),
          seasonId: getSeasonId(season),
          steps: sequence.steps,
        }).steps,
      }));
      payload.steps = payload.sequences[0].steps;
    }

    setSubmitting(true);
    try {
      if (isRecruitmentTab) {
        const recruitmentResult = await $apiV2.post(
          "/stats/player-recruitment-sequences",
          payload
        );
        setRecruitmentResponse(recruitmentResult?.data || { event_types: [] });
        setOpenRecruitmentPlayers({});
        setResponse(null);
        return;
      }

      const firstResult = await $apiV2.post("/stats/sequences_search", payload, {
        params: { page: 1, page_size: SEQUENCE_FETCH_PAGE_SIZE },
      });
      const firstData = firstResult?.data || {};
      const firstResults = Array.isArray(firstData.results)
        ? firstData.results
        : [];
      const totalCount = Number(firstData.count) || firstResults.length;
      const pageSize = Number(firstData.page_size) || SEQUENCE_FETCH_PAGE_SIZE;

      activeSearchPayloadRef.current = payload;
      setResponse({
        ...firstData,
        results: firstResults,
        count: totalCount,
        page_size: pageSize,
      });
      setResultsPage(1);
      setExcludedResultMarkers({});
      setAppliedImmediateEventsOnly(submittedImmediateEventsOnly);
      setAppliedSteps(submittedSteps);
      setAppliedPatternAnalysis(isPatternAnalysisTab);
    } catch (error) {
      const message = getSubmitErrorMessage(error, t);
      setSubmitError(message);
      addNotification(message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResultsPageChange = async (pageNumber) => {
    const payload = activeSearchPayloadRef.current;
    if (
      !payload ||
      resultsPageLoading ||
      pageNumber < 1 ||
      pageNumber > effectiveTotalPages ||
      pageNumber === resultsPage
    ) {
      return;
    }

    setResultsPageLoading(true);
    try {
      const pageResult = await $apiV2.post("/stats/sequences_search", payload, {
        params: { page: pageNumber, page_size: SEQUENCE_FETCH_PAGE_SIZE },
      });
      const pageData = pageResult?.data || {};
      const pageResults = Array.isArray(pageData.results)
        ? pageData.results
        : [];

      setResponse((currentResponse) => ({
        ...currentResponse,
        ...pageData,
        results: pageResults,
        count:
          Number(pageData.count) ||
          Number(currentResponse?.count) ||
          pageResults.length,
        page_size: Number(pageData.page_size) || SEQUENCE_FETCH_PAGE_SIZE,
      }));
      setResultsPage(pageNumber);
      setExcludedResultMarkers({});
      setActiveSequenceByMatch({});
      setOpenMarkerLists({});
    } catch (error) {
      const message = getSubmitErrorMessage(error, t);
      addNotification(message, "error");
    } finally {
      setResultsPageLoading(false);
    }
  };

  const toggleExcludedMarker = (resultKey, markerId) => {
    setExcludedResultMarkers((previousMarkers) => {
      const currentIds = new Set(previousMarkers[resultKey] || []);
      if (currentIds.has(markerId)) {
        currentIds.delete(markerId);
      } else {
        currentIds.add(markerId);
      }

      return {
        ...previousMarkers,
        [resultKey]: Array.from(currentIds),
      };
    });
  };

  const toggleMarkerList = (resultKey) => {
    setOpenMarkerLists((previous) => ({
      ...previous,
      [resultKey]: !previous[resultKey],
    }));
  };

  const startSequenceTutorial = useCallback(() => {
    // The tour spotlights the timeline/video controls, which only render on the
    // Smrt Sequences tab — start there so every step has something to highlight.
    setActiveTab(SEQUENCE_TAB_SEQUENCES);
    setTutorialStep(0);
    setTutorialRect(null);
    setShowTutorial(true);
  }, []);

  const completeSequenceTutorial = useCallback(() => {
    try {
      localStorage.setItem(SEQUENCE_TUTORIAL_STORAGE_KEY, "1");
    } catch {
      // noop
    }

    setShowTutorial(false);
    setTutorialRect(null);
  }, []);

  const handleTutorialNext = useCallback(() => {
    if (tutorialStep >= sequenceTutorialSteps.length - 1) {
      completeSequenceTutorial();
      return;
    }

    setTutorialStep((previousStep) => previousStep + 1);
  }, [completeSequenceTutorial, sequenceTutorialSteps.length, tutorialStep]);

  const handleTutorialSkip = useCallback(() => {
    completeSequenceTutorial();
  }, [completeSequenceTutorial]);

  const handleTutorialReplay = useCallback(() => {
    startSequenceTutorial();
  }, [startSequenceTutorial]);

  useEffect(() => {
    if (tutorialLaunchResolvedRef.current) {
      return;
    }

    tutorialLaunchResolvedRef.current = true;

    try {
      if (localStorage.getItem(SEQUENCE_TUTORIAL_STORAGE_KEY) !== "1") {
        startSequenceTutorial();
      }
    } catch {
      startSequenceTutorial();
    }
  }, [startSequenceTutorial]);

  const tutorialCurrentStep = showTutorial
    ? sequenceTutorialSteps[tutorialStep]
    : null;

  useEffect(() => {
    if (!showTutorial) {
      return undefined;
    }

    if (!tutorialCurrentStep?.spotlight) {
      setTutorialRect(null);
      return undefined;
    }

    const updateRect = () => {
      let nextRect = null;

      switch (tutorialCurrentStep.spotlight) {
        case "tabs":
          nextRect = mergeElementRects([tutorialTabsRef.current]);
          break;
        case "scope":
          nextRect = mergeElementRects([tutorialScopeRef.current]);
          break;
        case "matches":
          nextRect = mergeElementRects([tutorialMatchesRef.current]);
          break;
        case "videoMode":
          nextRect = mergeElementRects([tutorialVideoModeRef.current]);
          break;
        case "timeline":
          nextRect = mergeElementRects([tutorialTimelineRef.current]);
          break;
        case "templates":
          nextRect = mergeElementRects([tutorialTemplatesRef.current]);
          break;
        case "results":
          nextRect = mergeElementRects([tutorialResultsRef.current]);
          break;
        default:
          nextRect = null;
      }

      setTutorialRect(nextRect);
    };

    if (
      tutorialCurrentStep.spotlight === "timeline" &&
      tutorialTimelineRef.current
    ) {
      tutorialTimelineRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    if (
      tutorialCurrentStep.spotlight === "results" &&
      tutorialResultsRef.current
    ) {
      tutorialResultsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    // Template controls live at the top of the form — scroll back up to them
    // after the timeline step.
    if (
      tutorialCurrentStep.spotlight === "templates" &&
      tutorialTemplatesRef.current
    ) {
      tutorialTemplatesRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    const scrollDelay =
      tutorialCurrentStep.spotlight === "timeline" ||
      tutorialCurrentStep.spotlight === "templates" ||
      tutorialCurrentStep.spotlight === "results"
        ? 420
        : 70;
    const timeoutId = window.setTimeout(updateRect, scrollDelay);
    const animationFrameId =
      typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(updateRect)
        : null;

    window.addEventListener("resize", updateRect);
    window.addEventListener("scroll", updateRect, true);

    return () => {
      window.clearTimeout(timeoutId);
      if (
        animationFrameId !== null &&
        typeof window.cancelAnimationFrame === "function"
      ) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("resize", updateRect);
      window.removeEventListener("scroll", updateRect, true);
    };
  }, [
    showTutorial,
    tutorialCurrentStep,
    displayedSteps.length,
    sequenceResults.length,
  ]);

  const tutorialIsLastStep =
    tutorialStep >= sequenceTutorialSteps.length - 1;
  const tutorialSpotlightPadding = 10;
  const tutorialSpotlightStyle = tutorialRect
    ? {
        left: tutorialRect.left - tutorialSpotlightPadding,
        top: tutorialRect.top - tutorialSpotlightPadding,
        width: tutorialRect.width + tutorialSpotlightPadding * 2,
        height: tutorialRect.height + tutorialSpotlightPadding * 2,
      }
    : null;

  const { tutorialCardStyle, tutorialCardArrowSide } = (() => {
    const CARD_W = 360;
    // The card is content-box: width is the content, so its real footprint adds
    // the 26px horizontal padding (×2) and ~1.5px border (×2). Clamp against this
    // outer width so a right-edge spotlight (e.g. the templates step) doesn't push
    // the card off-screen.
    const CARD_OUTER_W = CARD_W + 52 + 3;
    const CARD_H = 238;
    const MARGIN = 16;
    const GAP = tutorialSpotlightPadding + 16;
    const vw = typeof window !== "undefined" ? window.innerWidth : CARD_W + 40;
    const vh = typeof window !== "undefined" ? window.innerHeight : 700;

    const mobileStyle = {
      position: "fixed",
      left: 12,
      right: 12,
      bottom: 12,
      top: "auto",
      transform: "none",
      width: "auto",
    };

    if (!tutorialCurrentStep) {
      return {
        tutorialCardStyle: {
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: CARD_W,
        },
        tutorialCardArrowSide: null,
      };
    }

    if (vw <= 480) {
      return { tutorialCardStyle: mobileStyle, tutorialCardArrowSide: null };
    }

    if (
      tutorialCurrentStep.id === "welcome" ||
      !tutorialRect ||
      tutorialCurrentStep.position === "center"
    ) {
      return {
        tutorialCardStyle: {
          position: "fixed",
          right: 24,
          bottom: 24,
          width: CARD_W,
          left: "auto",
          top: "auto",
          transform: "none",
        },
        tutorialCardArrowSide: null,
      };
    }

    let left = tutorialRect.left + tutorialRect.width / 2 - CARD_OUTER_W / 2;
    left = Math.max(MARGIN, Math.min(left, vw - CARD_OUTER_W - MARGIN));

    let top;
    let arrowSide;
    const spaceBelow = vh - (tutorialRect.bottom + GAP) - MARGIN;
    const spaceAbove = tutorialRect.top - GAP - MARGIN;

    if (tutorialCurrentStep.position === "below") {
      if (spaceBelow >= CARD_H) {
        top = tutorialRect.bottom + GAP;
        arrowSide = "top";
      } else {
        top = Math.max(MARGIN, tutorialRect.top - GAP - CARD_H);
        arrowSide = "bottom";
      }
    } else if (spaceAbove >= CARD_H) {
      top = tutorialRect.top - GAP - CARD_H;
      arrowSide = "bottom";
    } else {
      top = tutorialRect.bottom + GAP;
      arrowSide = "top";
    }

    top = Math.max(MARGIN, Math.min(top, vh - CARD_H - MARGIN));

    return {
      tutorialCardStyle: { position: "fixed", top, left, width: CARD_W },
      tutorialCardArrowSide: arrowSide,
    };
  })();

  return (
    <MainWrapper>
      <Navigation />
      <Page>
        <Header>
          <HeaderTop>
            <div>
              <Eyebrow>{t("sequenceSearch.eyebrow")}</Eyebrow>
              <Title>{t("sequenceSearch.title")}</Title>
            </div>
            <HeaderActions>
              <HeaderTemplateControls ref={tutorialTemplatesRef}>
                {isRecruitmentTab ? (
                  <SavedFilterTemplatesControlsView
                    {...playerRecruitmentTemplateControls}
                    pageKey={PLAYER_RECRUITMENT_TEMPLATE_PAGE_KEY}
                    handleOpenCreateModal={guardPlayerRecruitmentTemplateModalOpen(
                      playerRecruitmentTemplateControls.handleOpenCreateModal
                    )}
                    handleOpenUpdateModal={guardPlayerRecruitmentTemplateModalOpen(
                      playerRecruitmentTemplateControls.handleOpenUpdateModal
                    )}
                  />
                ) : (
                  <SavedFilterTemplatesControlsView
                    {...savedTemplateControls}
                    pageKey={SEQUENCE_TEMPLATE_PAGE_KEY}
                    handleOpenCreateModal={guardTemplateModalOpen(
                      savedTemplateControls.handleOpenCreateModal
                    )}
                    handleOpenUpdateModal={guardTemplateModalOpen(
                      savedTemplateControls.handleOpenUpdateModal
                    )}
                  />
                )}
              </HeaderTemplateControls>
              {!showTutorial && (
                <HowItWorksButton type="button" onClick={handleTutorialReplay}>
                  {t("sequenceSearch.tutorialHelp")}
                </HowItWorksButton>
              )}
            </HeaderActions>
          </HeaderTop>
          <Tabs ref={tutorialTabsRef}>
            {[
              {
                id: SEQUENCE_TAB_SEQUENCES,
                label: t("sequenceSearch.tabSequences"),
                subtitle: t("sequenceSearch.tabSequencesSubtitle"),
              },
              {
                id: SEQUENCE_TAB_OPPONENT,
                label: t("sequenceSearch.tabOpponentPatterns"),
                subtitle: t("sequenceSearch.tabOpponentSubtitle"),
              },
              {
                id: SEQUENCE_TAB_SIMILARITIES,
                label: t("sequenceSearch.tabSimilarities"),
                subtitle: t("sequenceSearch.tabSimilaritiesSubtitle"),
              },
              {
                id: SEQUENCE_TAB_PLAYER_RECRUITMENT,
                label: t("sequenceSearch.tabPlayerRecruitment"),
                subtitle: t("sequenceSearch.tabPlayerRecruitmentSubtitle"),
              },
            ].map((tab) => (
              <Tab
                key={tab.id}
                type="button"
                $active={activeTab === tab.id}
                onClick={() => handleTabChange(tab.id)}
              >
                <TabTitle>{tab.label}</TabTitle>
                <TabSubtitle>{tab.subtitle}</TabSubtitle>
              </Tab>
            ))}
          </Tabs>
        </Header>

        <PageScroll>
          {isRecruitmentTab ? (
            <PlayerRecruitmentSequences
              ref={playerRecruitmentSequencesRef}
              onTemplateFiltersChange={setPlayerRecruitmentTemplateFilters}
            />
          ) : (
          <Workspace>
            {isRecruitmentTab && (
              <>
                <RecruitmentPageHead>
                  <h2>Player Sequences</h2>
                  <p>Find the players who best match a tactical pattern</p>
                </RecruitmentPageHead>
                <RecruitmentStepper>
                  <RecruitmentStepperItem $active={validRecruitmentSequences.length === 0} $done={validRecruitmentSequences.length > 0}><b>1</b><span><strong>Build your sequences</strong><small>Up to 5 - choose the chains to search</small></span></RecruitmentStepperItem>
                  <RecruitmentStepperItem $active={validRecruitmentSequences.length > 0 && !(competition && season)} $done={Boolean(competition && season)}><b>2</b><span><strong>Set the search scope</strong><small>Choose competition and season</small></span></RecruitmentStepperItem>
                  <RecruitmentStepperItem $active={Boolean(recruitmentResponse)}><b>3</b><span><strong>Read the players</strong><small>Top 5 per event type, with clips</small></span></RecruitmentStepperItem>
                </RecruitmentStepper>
              </>
            )}
            <FormPanel onSubmit={handleSubmit}>
            {isRecruitmentTab && (
              <PlayerRecruitmentBuilder
                sequences={recruitmentSequences}
                eventOptions={sequenceEventOptions}
                onAddSequence={addRecruitmentSequence}
                onDeleteSequence={(sequenceId) => setRecruitmentSequences((current) => current.filter((sequence) => sequence.id !== sequenceId))}
                onUpdateSequence={updateRecruitmentSequence}
                onAddEvent={addRecruitmentEvent}
                onRemoveEvent={removeRecruitmentEvent}
              />
            )}
            <Section>
              {isRecruitmentTab && <RecruitmentScopeEyebrow><RecruitmentStepNumber>2</RecruitmentStepNumber> Step 2 of 3</RecruitmentScopeEyebrow>}
              <SectionTitle>{t("sequenceSearch.scope")}</SectionTitle>
              <ScopeGrid ref={tutorialScopeRef}>
                <SearchableSelect
                  label={t("sequenceSearch.competition")}
                  placeholder={t("sequenceSearch.searchCompetition")}
                  endpoint="/platform/competition"
                  params={{ page: 1, page_size: DEFAULT_PAGE_SIZE, sort: "alphabet" }}
                  value={competition}
                  onChange={handleCompetitionChange}
                  getLabel={getCompetitionLabel}
                  dataTestId="sequence-competition-input"
                  noResultsLabel={t("sequenceSearch.noOptions")}
                  clearLabel={t("sequenceSearch.clearSelection")}
                />
                <SearchableSelect
                  label={t("sequenceSearch.season")}
                  placeholder={
                    getOptionId(competition)
                      ? t("sequenceSearch.searchSeason")
                      : t("sequenceSearch.selectCompetitionFirst")
                  }
                  endpoint="/platform/year_season"
                  params={{
                    page: 1,
                    per_page: DEFAULT_PAGE_SIZE,
                    ...(getOptionId(competition)
                      ? { competitions_id: getOptionId(competition) }
                      : {}),
                  }}
                  value={season}
                  onChange={handleSeasonChange}
                  getLabel={getSeasonLabel}
                  getKey={getSeasonId}
                  dataTestId="sequence-season-input"
                  noResultsLabel={t("sequenceSearch.noOptions")}
                  clearLabel={t("sequenceSearch.clearSelection")}
                  transformOptions={seasonOptionsTransform}
                  enabled={Boolean(getOptionId(competition))}
                />
                <PlayerFieldStack>
                  <SearchableSelect
                    label={t("sequenceSearch.teamOptional")}
                    placeholder={
                      getOptionId(competition)
                        ? t("sequenceSearch.searchTeam")
                        : t("sequenceSearch.selectCompetitionFirst")
                    }
                    endpoint="/platform/team/"
                    params={{
                      page_size: DEFAULT_PAGE_SIZE,
                      ...(getOptionId(competition)
                        ? { competition_id: getOptionId(competition) }
                        : {}),
                      ...(getSeasonId(season)
                        ? { year_season_id: getSeasonId(season) }
                        : {}),
                    }}
                    value={null}
                    onChange={handleAddTeam}
                    getLabel={getTeamLabel}
                    dataTestId="sequence-team-input"
                    noResultsLabel={t("sequenceSearch.noOptions")}
                    clearLabel={t("sequenceSearch.clearSelection")}
                    enabled={Boolean(getOptionId(competition))}
                    minSearchLength={1}
                    localOptions={seasonTeamOptions}
                  />
                  {selectedTeams.length > 0 && (
                    <ChipList>
                      {selectedTeams.map((selectedTeam) => (
                        <PlayerChip key={getOptionId(selectedTeam)}>
                          {getTeamLabel(selectedTeam)}
                          <PlayerChipRemoveButton
                            type="button"
                            aria-label={`${t("sequenceSearch.removeTeam")}: ${getTeamLabel(selectedTeam)}`}
                            onClick={() => handleRemoveTeam(selectedTeam)}
                          >
                            x
                          </PlayerChipRemoveButton>
                        </PlayerChip>
                      ))}
                    </ChipList>
                  )}
                </PlayerFieldStack>

                {tabHasPlayerSearch && (
                  <PlayerFieldStack>
                    <SearchableSelect
                      label={t("sequenceSearch.playerOptional")}
                      placeholder={
                        getOptionId(competition)
                          ? t("sequenceSearch.searchPlayer")
                          : t("sequenceSearch.selectCompetitionFirst")
                      }
                      endpoint="/platform/player/"
                      params={{
                        page_size: DEFAULT_PAGE_SIZE,
                        ...(getOptionId(competition)
                          ? { competition_id: getOptionId(competition) }
                          : {}),
                        ...(getSeasonId(season)
                          ? { year_season_id: getSeasonId(season) }
                          : {}),
                        ...(selectedTeamIds.length === 1
                          ? { team_id: selectedTeamIds[0] }
                          : {}),
                      }}
                      value={null}
                      onChange={handleAddPlayer}
                      getLabel={getPlayerLabel}
                      dataTestId="sequence-player-input"
                      noResultsLabel={t("sequenceSearch.noOptions")}
                      clearLabel={t("sequenceSearch.clearSelection")}
                      enabled={Boolean(getOptionId(competition))}
                      minSearchLength={1}
                    />
                    {selectedPlayers.length > 0 && (
                      <ChipList>
                        {selectedPlayers.map((selectedPlayer) => (
                          <PlayerChip key={getOptionId(selectedPlayer)}>
                            {getPlayerLabel(selectedPlayer)}
                            <PlayerChipRemoveButton
                              type="button"
                              aria-label={`${t("sequenceSearch.removePlayer")}: ${getPlayerLabel(selectedPlayer)}`}
                              onClick={() => handleRemovePlayer(selectedPlayer)}
                            >
                              x
                            </PlayerChipRemoveButton>
                          </PlayerChip>
                        ))}
                      </ChipList>
                    )}
                  </PlayerFieldStack>
                )}
              </ScopeGrid>

              <Grid ref={tutorialMatchesRef}>
                <FieldGroup>
                  <Label>{t("sequenceSearch.matchPreset")}</Label>
                  <SearchBox ref={matchPresetDropdownRef}>
                    <DropdownButton
                      type="button"
                      disabled={
                        !matchControlsEnabled ||
                        matchesLoading ||
                        dateFilteredMatches.length === 0
                      }
                      onClick={() =>
                        setOpenMatchPresetDropdown((previousState) => !previousState)
                      }
                    >
                      <span>{selectedMatchPresetLabel}</span>
                      <span aria-hidden="true">
                        {openMatchPresetDropdown ? "^" : "v"}
                      </span>
                    </DropdownButton>
                    {openMatchPresetDropdown && (
                      <OptionsList>
                        {matchPresetOptions.map((option) => (
                          <DropdownOption
                            key={option.id}
                            type="button"
                            $selected={selectedMatchCount?.id === option.id}
                            onClick={() => {
                              setSelectedMatchCount(option);
                              setOpenMatchPresetDropdown(false);
                            }}
                          >
                            {option.title}
                          </DropdownOption>
                        ))}
                      </OptionsList>
                    )}
                  </SearchBox>
                </FieldGroup>
                <FieldGroup>
                  <Label>{t("sequenceSearch.matches")}</Label>
                  <SearchBox ref={matchesDropdownRef}>
                    <DropdownButton
                      type="button"
                      disabled={
                        !matchControlsEnabled ||
                        matchesLoading ||
                        dateFilteredMatches.length === 0
                      }
                      onClick={() =>
                        setOpenMatchesDropdown((previousState) => !previousState)
                      }
                    >
                      <span>{selectedMatchesLabel}</span>
                      <span aria-hidden="true">
                        {openMatchesDropdown ? "^" : "v"}
                      </span>
                    </DropdownButton>
                    {openMatchesDropdown && (
                      <OptionsList>
                        <DropdownOption
                          type="button"
                          $selected={selectedMatches.length === 0}
                          onClick={handleDeselectAllMatches}
                        >
                          {t("sequenceSearch.deselectAllMatches")}
                        </DropdownOption>
                        {dateFilteredMatches.length > 0 ? (
                          dateFilteredMatches.map((match) => (
                            <DropdownOption
                              key={match.id}
                              type="button"
                              $selected={selectedMatchIdSet.has(String(match.id))}
                              onClick={() => handleMatchToggle(match)}
                            >
                              {formatMatchSelectLabel(match)}
                            </DropdownOption>
                          ))
                        ) : (
                          <DropdownOption type="button" $disabled>
                            {t("sequenceSearch.noMatches")}
                          </DropdownOption>
                        )}
                      </OptionsList>
                    )}
                  </SearchBox>
                  {!matchControlsEnabled && (
                    <FieldHint>
                      {t("sequenceSearch.selectTeamForMatches")}
                    </FieldHint>
                  )}
                  {matchesError && <ErrorText>{matchesError}</ErrorText>}
                </FieldGroup>
                <DateRangeField>
                  <DateRangeSlider
                    matches={competitionScopedMatches}
                    selectedMatchIds={selectedMatchIds}
                    onRangeChange={handleDateRangeChange}
                  />
                </DateRangeField>
                <FieldGroup ref={tutorialVideoModeRef}>
                  <Label>{t("sequenceSearch.videoPlaybackMode")}</Label>
                  <ModeToggleGroup>
                    <ModeToggleButton
                      type="button"
                      $selected={videoPlaybackMode === SEQUENCE_VIDEO_MODE_SEPARATE}
                      onClick={() =>
                        setVideoPlaybackMode(SEQUENCE_VIDEO_MODE_SEPARATE)
                      }
                    >
                      {t("sequenceSearch.separateVideos")}
                    </ModeToggleButton>
                    <ModeToggleButton
                      type="button"
                      $selected={videoPlaybackMode === SEQUENCE_VIDEO_MODE_SEQUENCE}
                      onClick={() =>
                        setVideoPlaybackMode(SEQUENCE_VIDEO_MODE_SEQUENCE)
                      }
                    >
                      {t("sequenceSearch.sequenceVideo")}
                    </ModeToggleButton>
                  </ModeToggleGroup>
                </FieldGroup>
                {tabHasTimeline && !isRecruitmentTab && (
                  <>
                <FieldGroup>
                  <Label>{t("sequenceSearch.immediateEventsOnly")}</Label>
                  <ImmediateToggleButton
                    type="button"
                    $active={immediateEventsOnly}
                    onClick={() =>
                      setImmediateEventsOnly((currentValue) => !currentValue)
                    }
                  >
                    {t("sequenceSearch.immediateEventsOnly")}
                  </ImmediateToggleButton>
                  <FieldHint>
                    {t("sequenceSearch.immediateEventsOnlyHint")}
                  </FieldHint>
                </FieldGroup>
                <FieldGroup>
                  <Label>{t("sequenceSearch.maxEventsBetween")}</Label>
                  <TextInput
                    value={maxEventsBetween}
                    type="number"
                    min="1"
                    placeholder={t("sequenceSearch.maxEventsBetweenPlaceholder")}
                    onChange={(event) =>
                      setMaxEventsBetween(event.target.value)
                    }
                  />
                </FieldGroup>
                  </>
                )}
                {isRecruitmentTab && (
                  <FieldGroup>
                    <Label>{t("sequenceSearch.recruitmentMatching")}</Label>
                    <FieldHint>{t("sequenceSearch.recruitmentImmediateHint")}</FieldHint>
                  </FieldGroup>
                )}
                {tabHasChainRepetitions && (
                <FieldGroup>
                  <Label>{t("sequenceSearch.sequenceCountLabel")}</Label>
                  <InlineFieldRow>
                    <TextInput
                      value={minSequenceCountPerMatch}
                      type="number"
                      min="1"
                      placeholder={t("sequenceSearch.sequenceCountFromPlaceholder")}
                      onChange={(event) =>
                        setMinSequenceCountPerMatch(event.target.value)
                      }
                    />
                    <TextInput
                      value={maxSequenceCountPerMatch}
                      type="number"
                      min="1"
                      placeholder={t("sequenceSearch.sequenceCountToPlaceholder")}
                      onChange={(event) =>
                        setMaxSequenceCountPerMatch(event.target.value)
                      }
                    />
                  </InlineFieldRow>
                  <FieldHint>
                    {t("sequenceSearch.sequenceCountPerMatchHint")}
                  </FieldHint>
                </FieldGroup>
                )}
              </Grid>
            </Section>

            {tabHasTimeline && !isRecruitmentTab && (
            <Section ref={tutorialTimelineRef}>
              <SectionHeader>
                <SectionTitle>{t("sequenceSearch.timeline")}</SectionTitle>
                <SectionHeaderActions>
                  {displayedSteps.length > 0 && (
                    <StepSummary>{t("sequenceSearch.dragToReorder")}</StepSummary>
                  )}
                  {steps.length > 0 && (
                    <ResetStepsButton
                      type="button"
                      data-testid="sequence-reset-steps"
                      onClick={handleResetSteps}
                    >
                      {t("sequenceSearch.resetSteps")}
                    </ResetStepsButton>
                  )}
                </SectionHeaderActions>
              </SectionHeader>

              {displayedSteps.length === 0 && (
                <TimelineExampleHint>
                  <span>{t("sequenceSearch.timelineExampleHint")}</span>
                  <ExampleDropdown ref={exampleMenuRef}>
                    <LoadExampleButton
                      type="button"
                      $open={exampleMenuOpen}
                      onClick={() => setExampleMenuOpen((open) => !open)}
                    >
                      {t("sequenceSearch.loadExample")}
                      <span aria-hidden="true">
                        {exampleMenuOpen ? " ^" : " v"}
                      </span>
                    </LoadExampleButton>
                    {exampleMenuOpen && (
                      <ExampleMenu>
                        {EXAMPLE_SEQUENCE_PRESET_KEYS.map((preset) => (
                          <ExampleMenuItem
                            key={preset}
                            type="button"
                            onClick={() => {
                              handleLoadExample(preset);
                              setExampleMenuOpen(false);
                            }}
                          >
                            {t(`sequenceSearch.example_${preset}`)}
                          </ExampleMenuItem>
                        ))}
                      </ExampleMenu>
                    )}
                  </ExampleDropdown>
                </TimelineExampleHint>
              )}

              <StepList>
                {displayedSteps.map((step, index) => {
                  const isTutorialDemoStep = shouldShowTutorialDemoSteps;

                  return (
                    <React.Fragment key={step.id}>
                      {index > 0 && (
                        <StepArrow aria-hidden="true">→</StepArrow>
                      )}
                      <StepItem
                        draggable={!isTutorialDemoStep}
                      onDragStart={() => {
                        if (!isTutorialDemoStep) {
                          setDraggedStepIndex(index);
                        }
                      }}
                      onDragOver={(event) => event.preventDefault()}
                      onDrop={() => {
                        if (!isTutorialDemoStep && draggedStepIndex !== null) {
                          setSteps((previousSteps) =>
                            moveItem(previousSteps, draggedStepIndex, index)
                          );
                          setDraggedStepIndex(null);
                        }
                      }}
                    >
                      <StepCardHeader>
                        <StepOrder>{index + 1}</StepOrder>
                        <RemoveButton
                          type="button"
                          aria-label={t("sequenceSearch.removeStep")}
                          disabled={isTutorialDemoStep}
                          onClick={() =>
                            !isTutorialDemoStep && removeStep(step.id)
                          }
                        >
                          x
                        </RemoveButton>
                      </StepCardHeader>
                      <StepSource>{step.sourceLabel}</StepSource>
                      <StepMeta>
                        {step.stepIdField}: {step.idsInput}
                        {step.sourceGroup ? ` | ${step.sourceGroup}` : ""}
                      </StepMeta>
                      <StepGrid>
                        <TeamChoiceButton
                          type="button"
                          $active={step.team_link === "selected_team"}
                          disabled={isTutorialDemoStep}
                          onClick={() =>
                            !isTutorialDemoStep &&
                            updateStep(step.id, { team_link: "selected_team" })
                          }
                        >
                          {t("sequenceSearch.selectedTeam")}
                        </TeamChoiceButton>
                        <TeamChoiceButton
                          type="button"
                          $active={step.team_link === "opponent"}
                          disabled={isTutorialDemoStep}
                          onClick={() =>
                            !isTutorialDemoStep &&
                            updateStep(step.id, { team_link: "opponent" })
                          }
                        >
                          {t("sequenceSearch.opponentTeam")}
                        </TeamChoiceButton>
                      </StepGrid>
                      <StepCheckboxLabel>
                        <input
                          type="checkbox"
                          checked={step.exclude === true}
                          disabled={isTutorialDemoStep}
                          onChange={(event) =>
                            !isTutorialDemoStep &&
                            updateStep(step.id, {
                              exclude: event.target.checked,
                            })
                          }
                        />
                        <span>{t("sequenceSearch.excludeStep")}</span>
                      </StepCheckboxLabel>
                      </StepItem>
                    </React.Fragment>
                  );
                })}
                <AddStepWrapper ref={eventPickerRef}>
                  <AddStepCard
                    type="button"
                    data-testid="sequence-add-step"
                    $open={eventPickerOpen}
                    onClick={(event) => {
                      // The picker mounts during this click. Prevent that same
                      // event from reaching its document-level outside-click
                      // listener and immediately closing the options list.
                      event.stopPropagation();
                      setEventPickerOpen(true);
                    }}
                  >
                    <span aria-hidden="true">+</span>
                    <span>{t("sequenceSearch.addStep")}</span>
                  </AddStepCard>
                  {eventPickerOpen && (
                    <EventPickerPopup>
                      <SearchableSelect
                        label={t("sequenceSearch.eventSearch")}
                        placeholder={t("sequenceSearch.eventSearchPlaceholder")}
                        endpoint="/stats/marker_actions"
                        params={{
                          page: 1,
                          page_size: 250,
                          events_only: 1,
                        }}
                        value={null}
                        onChange={(option) => {
                          handleAddEventOption(option);
                          setEventPickerOpen(false);
                        }}
                        getLabel={(option) => option.label}
                        getKey={(option) => option.id}
                        dataTestId="sequence-event-search-input"
                        noResultsLabel={t("sequenceSearch.noOptions")}
                        clearLabel={t("sequenceSearch.clearSelection")}
                        minSearchLength={0}
                        openOnMount
                        apiClient={$apiV2}
                        localOptions={sequenceEventOptions}
                      />
                    </EventPickerPopup>
                  )}
                </AddStepWrapper>
              </StepList>
            </Section>
            )}

            {submitError && <ErrorBanner>{submitError}</ErrorBanner>}

            <Actions>
              {scopeSummaryText && (
                <ScopeSummaryChip>{scopeSummaryText}</ScopeSummaryChip>
              )}
              <SecondaryButton
                type="button"
                onClick={() => {
                  setResponse(null);
                  setRecruitmentResponse(null);
                  setOpenRecruitmentPlayers({});
                  setResultsPage(1);
                  activeSearchPayloadRef.current = null;
                  setSubmitError("");
                  setAppliedImmediateEventsOnly(false);
                  setAppliedSteps([]);
                  setAppliedPatternAnalysis(false);
                }}
              >
                {t("sequenceSearch.clearResults")}
              </SecondaryButton>
              <PrimaryButton type="submit" disabled={submitting}>
                {submitting
                  ? t("sequenceSearch.searching")
                  : isRecruitmentTab
                    ? t("sequenceSearch.searchPlayers")
                    : t("sequenceSearch.search")}
              </PrimaryButton>
            </Actions>
          </FormPanel>

          <ResultsPanel ref={tutorialResultsRef}>
            {isRecruitmentTab && <RecruitmentScopeEyebrow><RecruitmentStepNumber>3</RecruitmentStepNumber> Step 3 of 3</RecruitmentScopeEyebrow>}
            <ResultsStickyHeader>
              <SectionTitle>{t("sequenceSearch.results")}</SectionTitle>
              {isRecruitmentTab && recruitmentResponse && (
                <ResultsTotals>
                  <ResultsTotalStat>
                    <span>{t("sequenceSearch.totalSequencesFound")}</span>
                    <strong>{recruitmentResponse.matched_sequences || 0}</strong>
                  </ResultsTotalStat>
                </ResultsTotals>
              )}
              {!isRecruitmentTab && displayedResponse && sequenceResults.length > 0 && (
                <ResultsTotals>
                  <ResultsTotalStat>
                    <span>{t("sequenceSearch.totalMatches")}</span>
                    <strong>{totalMatchesFound}</strong>
                  </ResultsTotalStat>
                  <ResultsTotalStat>
                    <span>{t("sequenceSearch.totalSequencesFound")}</span>
                    <strong>{totalSequencesFound}</strong>
                  </ResultsTotalStat>
                </ResultsTotals>
              )}
            </ResultsStickyHeader>
            {isRecruitmentTab ? (
              !recruitmentResponse ? (
                <EmptyState>{t("sequenceSearch.recruitmentEmptyState")}</EmptyState>
              ) : (recruitmentResponse.event_types || []).length === 0 ? (
                <EmptyState>{t("sequenceSearch.noResults")}</EmptyState>
              ) : (
                (recruitmentResponse.event_types || []).map((eventType) => (
                  <ResultGroup key={eventType.event_type}>
                    <ResultHeader>
                      <div>
                        <ResultTitle>{eventType.event_type}</ResultTitle>
                        <ResultMeta>{t("sequenceSearch.recruitmentTopPlayers")}</ResultMeta>
                      </div>
                      <SequencePill>{eventType.players.length} {t("sequenceSearch.players")}</SequencePill>
                    </ResultHeader>
                    <RecruitmentTable>
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>{t("sequenceSearch.playerOptional")}</th>
                          <th>{t("sequenceSearch.recruitmentOccurrences")}</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {eventType.players.map((player, index) => {
                          const playerKey = `${eventType.event_type}:${player.id}`;
                          const isOpen = openRecruitmentPlayers[playerKey] === true;
                          return (
                            <React.Fragment key={playerKey}>
                              <RecruitmentPlayerRow
                                $open={isOpen}
                                onClick={() => setOpenRecruitmentPlayers((current) => ({ ...current, [playerKey]: !isOpen }))}
                              >
                                <td>{index + 1}</td>
                                <td>{player.display_name}</td>
                                <td>{player.occurrences}</td>
                                <td>{isOpen ? "⌄" : "›"}</td>
                              </RecruitmentPlayerRow>
                              {isOpen && (
                                <tr>
                                  <td colSpan="4">
                                    <RecruitmentOccurrenceList>
                                      {player.sequences.map((occurrence, occurrenceIndex) => (
                                        <RecruitmentOccurrence key={`${playerKey}:${occurrence.marker_id}:${occurrenceIndex}`}>
                                          <span>{formatMatchTitle(occurrence.match || {}, t("sequenceSearch.unknownMatch"))}</span>
                                          <OpenEventsButton
                                            type="button"
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              openVideoList(
                                                (occurrence.marker_ids || []).join(","),
                                                `${player.display_name} - ${eventType.event_type}`,
                                                false,
                                                occurrence.marker_ids || [],
                                                true,
                                                true
                                              );
                                            }}
                                            disabled={(occurrence.marker_ids || []).length === 0}
                                          >
                                            {t("sequenceSearch.openSequenceVideo")}
                                          </OpenEventsButton>
                                        </RecruitmentOccurrence>
                                      ))}
                                    </RecruitmentOccurrenceList>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </RecruitmentTable>
                  </ResultGroup>
                ))
              )
            ) : !displayedResponse ? (
              <EmptyState>{t("sequenceSearch.emptyState")}</EmptyState>
            ) : sequenceResults.length === 0 ? (
              <EmptyState>{t("sequenceSearch.noResults")}</EmptyState>
            ) : appliedPatternAnalysis ? (
              <>
                {paginatedOpponentMatchGroups.map((group) => (
                  <ResultGroup key={group.matchKey}>
                    <ResultHeader>
                      <div>
                        <ResultTitle>
                          {formatMatchTitle(
                            group.match,
                            group.match.id
                              ? `${t("sequenceSearch.match")} #${group.match.id}`
                              : t("sequenceSearch.unknownMatch")
                          )}
                        </ResultTitle>
                        <ResultMeta>
                          {[
                            formatEntityName(group.match.competition),
                            formatDateValue(group.match.date),
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </ResultMeta>
                      </div>
                      <SequencePill>
                        {group.results.length} {t("sequenceSearch.sequences")}
                      </SequencePill>
                    </ResultHeader>
                    {group.patterns.map((pattern, patternIndex) => {
                      const patternActionsText = pattern.actions.join(" → ");
                      const firstPatternMarker = (pattern.occurrences[0]
                        ?.markers || [])[0];
                      const patternTeamLabel = firstPatternMarker
                        ? getMarkerTeamLabel(firstPatternMarker, group.match)
                        : "";
                      const patternSummary =
                        patternTeamLabel &&
                        !patternTeamLabel.startsWith("Team #")
                          ? t("sequenceSearch.patternSummary", {
                              team: patternTeamLabel,
                              pattern: patternActionsText,
                              count: pattern.count,
                            })
                          : t("sequenceSearch.patternSummaryNoTeam", {
                              pattern: patternActionsText,
                              count: pattern.count,
                            });
                      return (
                        <PatternBlock
                          key={`${group.matchKey}-pattern-${patternIndex}`}
                        >
                          <PatternSummary>{patternSummary}</PatternSummary>
                          <PatternHeader>
                          <StepsRow>
                            {pattern.actions.map((action, actionIndex) => (
                              <React.Fragment
                                key={`${group.matchKey}-${patternIndex}-${actionIndex}`}
                              >
                                {actionIndex > 0 && (
                                  <StepPillArrow aria-hidden="true">
                                    →
                                  </StepPillArrow>
                                )}
                                <StepPill>
                                  <StepPillNum>{actionIndex + 1}</StepPillNum>
                                  <StepPillLabel>{action}</StepPillLabel>
                                </StepPill>
                              </React.Fragment>
                            ))}
                          </StepsRow>
                          <RepeatBadge>×{pattern.count}</RepeatBadge>
                        </PatternHeader>
                        <OccurrenceList>
                          {pattern.occurrences.map(
                            (occurrence, occurrenceIndex) => {
                              const occurrenceMarkers =
                                getResultMarkers(occurrence);
                              const occurrenceTitle = buildOpenEventsTitle({
                                group,
                                result: occurrence,
                                index: occurrenceIndex,
                              });
                              const sequenceTitle = buildSequenceMarkerPairTitle(
                                getResultMarkerRecords(occurrence),
                                occurrenceMarkers
                              );
                              return (
                                <OccurrenceRow
                                  key={`${group.matchKey}-${patternIndex}-occ-${occurrenceIndex}`}
                                >
                                  <OccurrenceLabel>
                                    {t("sequenceSearch.repetition")}{" "}
                                    {occurrenceIndex + 1}
                                    {" · "}
                                    {formatCompactValue(
                                      getResultDuration(occurrence)
                                    )}
                                    s
                                  </OccurrenceLabel>
                                  <OpenEventsButton
                                    type="button"
                                    onClick={() =>
                                      handleOpenResultVideo({
                                        markers: occurrenceMarkers,
                                        title: occurrenceTitle,
                                        allMarkers: occurrenceMarkers,
                                        sequenceTitle,
                                      })
                                    }
                                    disabled={occurrenceMarkers.length === 0}
                                  >
                                    {videoActionLabel}
                                  </OpenEventsButton>
                                </OccurrenceRow>
                              );
                            }
                          )}
                        </OccurrenceList>
                      </PatternBlock>
                      );
                    })}
                  </ResultGroup>
                ))}
                {opponentTotalPages > 1 && (
                  <PaginationBar>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleResultsPageChange(resultsPage - 1)}
                      disabled={resultsPage === 1 || resultsPageLoading}
                    >
                      {t("sequenceSearch.previousPage")}
                    </SecondaryButton>
                    <PaginationStatus>
                      {t("sequenceSearch.page")} {resultsPage}{" "}
                      {t("sequenceSearch.of")} {opponentTotalPages}
                    </PaginationStatus>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleResultsPageChange(resultsPage + 1)}
                      disabled={
                        resultsPage === opponentTotalPages || resultsPageLoading
                      }
                    >
                      {t("sequenceSearch.nextPage")}
                    </SecondaryButton>
                  </PaginationBar>
                )}
              </>
            ) : (
              <>
                {groupedResults.map((group) => (
                  <ResultGroup key={group.matchKey}>
                    <ResultHeader>
                      <div>
                        <ResultTitle>
                          {formatMatchTitle(
                            group.match,
                            group.match.id
                              ? `${t("sequenceSearch.match")} #${group.match.id}`
                              : t("sequenceSearch.unknownMatch")
                          )}
                        </ResultTitle>
                        <ResultMeta>
                          {[formatEntityName(group.match.competition), formatDateValue(group.match.date)]
                            .filter(Boolean)
                            .join(" | ")}
                        </ResultMeta>
                      </div>
                      <SequencePill>
                        {group.sequence_ids.length}{" "}
                        {t("sequenceSearch.sequences")}
                      </SequencePill>
                    </ResultHeader>
                    <SeqChipsRow>
                      <SeqChipsLabel>
                        {t("sequenceSearch.sequences")}
                      </SeqChipsLabel>
                      {group.results.map((result, index) => {
                        const chipSeqId = getSequenceId(result);
                        const chipActiveIndex = Math.min(
                          activeSequenceByMatch[group.matchKey] ?? 0,
                          group.results.length - 1
                        );
                        return (
                          <SeqChip
                            key={`${group.matchKey}-chip-${index}`}
                            type="button"
                            $active={index === chipActiveIndex}
                            onClick={() =>
                              setActiveSequenceByMatch((previous) => ({
                                ...previous,
                                [group.matchKey]: index,
                              }))
                            }
                          >
                            {chipSeqId || index + 1}
                          </SeqChip>
                        );
                      })}
                    </SeqChipsRow>
                    {(() => {
                      const index = Math.min(
                        activeSequenceByMatch[group.matchKey] ?? 0,
                        group.results.length - 1
                      );
                      const result = group.results[index];
                      if (!result) {
                        return null;
                      }
                      const resultMarkers = getResultMarkers(result);
                      const markerRecords = getResultMarkerRecords(result);
                      const matchedSteps = getMatchedSteps(result);
                      // Render the steps that were submitted (so excluded
                      // events still show), falling back to the live form for
                      // the tutorial demo where nothing was submitted.
                      const requestedResultSteps =
                        appliedSteps.length > 0 ? appliedSteps : displayedSteps;
                      const players = getResultPlayers(result);
                      const sequenceId = getSequenceId(result);
                      const resultKey = getResultExclusionKey(
                        group.matchKey,
                        result,
                        index
                      );
                      const excludedMarkerIds = excludedResultMarkers[resultKey] || [];
                      const excludedMarkerIdSet = new Set(excludedMarkerIds);
                      const mergeMarkers = resultMarkers.filter(
                        (markerId) => !excludedMarkerIdSet.has(String(markerId))
                      );
                      const openTitle = buildOpenEventsTitle({
                        group,
                        result,
                        index,
                      });
                      // Sequence videos are named after the first and last
                      // kept markers, e.g. "Pass forward - Goal".
                      const sequenceTitle = buildSequenceMarkerPairTitle(
                        markerRecords,
                        mergeMarkers
                      );
                      const markersOpen = openMarkerLists[resultKey] === true;

                      return (
                        <SequenceBlock>
                          <SequenceRowHeader>
                            <SequenceRowTitle>
                              {sequenceId
                                ? `${t("sequenceSearch.sequence")} #${sequenceId}`
                                : `${t("sequenceSearch.sequence")} ${index + 1}`}
                            </SequenceRowTitle>
                            <SequenceRowActions>
                              <MiniStat>
                                {formatCompactValue(getResultDuration(result))}s
                              </MiniStat>
                              <OpenEventsButton
                                type="button"
                                onClick={() =>
                                  handleOpenResultVideo({
                                    markers: mergeMarkers,
                                    title: openTitle,
                                    allMarkers: resultMarkers,
                                    sequenceTitle,
                                  })
                                }
                                disabled={mergeMarkers.length === 0}
                              >
                                {videoActionLabel}
                              </OpenEventsButton>
                            </SequenceRowActions>
                          </SequenceRowHeader>

                          <StepsRow>
                            {(() => {
                              const orderedMatchedSteps = [...matchedSteps].sort(
                                (left, right) =>
                                  Number(left.index) - Number(right.index)
                              );

                              const markerCountLabel = (count) =>
                                count > 0
                                  ? `${count} ${t("sequenceSearch.markers").toLowerCase()}`
                                  : "";

                              const renderPill = ({
                                key,
                                order,
                                title,
                                count,
                                excluded,
                                timeRange,
                              }) => (
                                <React.Fragment key={key}>
                                  {order > 1 && (
                                    <StepPillArrow aria-hidden="true">
                                      →
                                    </StepPillArrow>
                                  )}
                                  <StepPill
                                    $excluded={excluded}
                                    title={timeRange || undefined}
                                  >
                                    <StepPillNum>{order}</StepPillNum>
                                    <StepPillLabel>{title}</StepPillLabel>
                                    {count ? (
                                      <StepPillCount>{count}</StepPillCount>
                                    ) : null}
                                    {excluded && (
                                      <StepExcludedBadge>
                                        {t("sequenceSearch.excluded")}
                                      </StepExcludedBadge>
                                    )}
                                  </StepPill>
                                </React.Fragment>
                              );

                              // Older results without a submitted-step snapshot
                              // fall back to listing the API steps as-is.
                              if (requestedResultSteps.length === 0) {
                                return orderedMatchedSteps.map(
                                  (step, stepIndex) => {
                                    const stepMarkers = normalizeIdList(
                                      step.marker_ids || step.markers
                                    );
                                    return renderPill({
                                      key: `${group.matchKey}-${index}-${stepIndex}`,
                                      order: stepIndex + 1,
                                      title: getStepTitle(step, displayedSteps),
                                      count: markerCountLabel(stepMarkers.length),
                                      excluded: false,
                                      timeRange: getStepTimeRange(step),
                                    });
                                  }
                                );
                              }

                              // List every submitted step. The API omits
                              // excluded events from matched_steps, so consume
                              // the matched steps in order across the kept
                              // events only — this lines each event up with its
                              // markers no matter how the API numbers them.
                              let matchedPointer = 0;
                              return requestedResultSteps.map(
                                (requestedStep, stepIndex) => {
                                  const isExcludedStep =
                                    requestedStep?.exclude === true;
                                  const matchedStep = isExcludedStep
                                    ? null
                                    : orderedMatchedSteps[matchedPointer++] ||
                                      null;
                                  const stepMarkers = matchedStep
                                    ? normalizeIdList(
                                        matchedStep.marker_ids ||
                                          matchedStep.markers
                                      )
                                    : [];
                                  const stepTitle =
                                    requestedStep?.sourceLabel ||
                                    getStepTitle(
                                      matchedStep || { index: stepIndex },
                                      requestedResultSteps
                                    );
                                  return renderPill({
                                    key: `${group.matchKey}-${index}-${stepIndex}`,
                                    order: stepIndex + 1,
                                    title: stepTitle,
                                    count: markerCountLabel(stepMarkers.length),
                                    excluded: isExcludedStep,
                                    timeRange: matchedStep
                                      ? getStepTimeRange(matchedStep)
                                      : "",
                                  });
                                }
                              );
                            })()}
                          </StepsRow>

                          <ResultSummaryGrid>
                            <ResultSummaryItem>
                              <strong>{t("sequenceSearch.markers")}</strong>
                              <span>{resultMarkers.length}</span>
                              {excludedMarkerIds.length > 0 && (
                                <small>
                                  {mergeMarkers.length} / {resultMarkers.length}
                                </small>
                              )}
                            </ResultSummaryItem>
                            <ResultSummaryItem>
                              <strong>{t("sequenceSearch.players")}</strong>
                              <span>{players.length}</span>
                            </ResultSummaryItem>
                          </ResultSummaryGrid>

                          {markerRecords.length > 0 && (
                            <MarkerListSection>
                              <MarkersToggle
                                type="button"
                                $open={markersOpen}
                                onClick={() => toggleMarkerList(resultKey)}
                              >
                                <span>{t("sequenceSearch.allMarkers")}</span>
                                <MarkersChevron
                                  $open={markersOpen}
                                  aria-hidden="true"
                                >
                                  ▾
                                </MarkersChevron>
                              </MarkersToggle>
                              {markersOpen && (
                              <MarkerList>
                                {markerRecords.map((marker, markerIndex) => {
                                  const markerId = getMarkerId(marker);
                                  const isExcluded =
                                    markerId && excludedMarkerIdSet.has(markerId);
                                  const markerPlayerLabel = getMarkerPlayerLabel(
                                    marker,
                                    result.players
                                  );
                                  const markerTeamLabel = getMarkerTeamLabel(
                                    marker,
                                    group.match
                                  );
                                  const markerMeta = [
                                    formatMarkerSecond(marker.second),
                                    markerId ? `#${markerId}` : "",
                                    markerPlayerLabel,
                                    markerTeamLabel,
                                  ].filter(Boolean);

                                  return (
                                    <MarkerListItem
                                      key={`${resultKey}-${markerId || markerIndex}`}
                                      $excluded={isExcluded}
                                    >
                                      <MarkerListContent>
                                        <MarkerAction>
                                          {marker.parameter_name ||
                                            marker.action ||
                                            marker.name ||
                                            `${t("sequenceSearch.markers")} ${markerIndex + 1}`}
                                        </MarkerAction>
                                        <MarkerMeta>
                                          {markerMeta.join(" | ")}
                                        </MarkerMeta>
                                        {isExcluded && (
                                          <MarkerExcludedText>
                                            {t("sequenceSearch.excludedFromMerge")}
                                          </MarkerExcludedText>
                                        )}
                                      </MarkerListContent>
                                      {markerId && (
                                        <MarkerRemoveButton
                                          type="button"
                                          aria-label={
                                            isExcluded
                                              ? t("sequenceSearch.restoreToMerge")
                                              : t("sequenceSearch.excludeFromMerge")
                                          }
                                          title={
                                            isExcluded
                                              ? t("sequenceSearch.restoreToMerge")
                                              : t("sequenceSearch.excludeFromMerge")
                                          }
                                          onClick={() =>
                                            toggleExcludedMarker(resultKey, markerId)
                                          }
                                        >
                                          {isExcluded ? "+" : "x"}
                                        </MarkerRemoveButton>
                                      )}
                                    </MarkerListItem>
                                  );
                                })}
                              </MarkerList>
                              )}
                            </MarkerListSection>
                          )}
                        </SequenceBlock>
                      );
                    })()}
                  </ResultGroup>
                ))}
                {totalResultPages > 1 && (
                  <PaginationBar>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleResultsPageChange(resultsPage - 1)}
                      disabled={resultsPage === 1 || resultsPageLoading}
                    >
                      {t("sequenceSearch.previousPage")}
                    </SecondaryButton>
                    <PaginationStatus>
                      {t("sequenceSearch.page")} {resultsPage}{" "}
                      {t("sequenceSearch.of")} {totalResultPages}
                    </PaginationStatus>
                    <SecondaryButton
                      type="button"
                      onClick={() => handleResultsPageChange(resultsPage + 1)}
                      disabled={
                        resultsPage === totalResultPages || resultsPageLoading
                      }
                    >
                      {t("sequenceSearch.nextPage")}
                    </SecondaryButton>
                  </PaginationBar>
                )}
              </>
            )}
          </ResultsPanel>
          </Workspace>
          )}
        </PageScroll>
      </Page>
      {showTutorial && tutorialCurrentStep ? (
        <>
          {tutorialCurrentStep.spotlight && tutorialRect ? (
            <svg
              style={{
                position: "fixed",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "auto",
                zIndex: SEQUENCE_TUTORIAL_OVERLAY_Z_INDEX,
              }}
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <mask id="sequence-search-tutorial-spotlight-mask">
                  <rect width="100%" height="100%" fill="white" />
                  <rect
                    x={tutorialRect.left - tutorialSpotlightPadding}
                    y={tutorialRect.top - tutorialSpotlightPadding}
                    width={tutorialRect.width + tutorialSpotlightPadding * 2}
                    height={tutorialRect.height + tutorialSpotlightPadding * 2}
                    rx="12"
                    fill="black"
                  />
                </mask>
              </defs>
              <rect
                width="100%"
                height="100%"
                fill="rgba(0,0,0,0.78)"
                mask="url(#sequence-search-tutorial-spotlight-mask)"
              />
            </svg>
          ) : (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.82)",
                zIndex: SEQUENCE_TUTORIAL_OVERLAY_Z_INDEX,
                pointerEvents: "auto",
              }}
            />
          )}

          {tutorialSpotlightStyle ? (
            <div
              style={{
                position: "fixed",
                ...tutorialSpotlightStyle,
                borderRadius: 14,
                border: `2px solid ${SEQUENCE_TUTORIAL_ACCENT}`,
                boxShadow: `0 0 0 1px ${SEQUENCE_TUTORIAL_ACCENT}44, 0 0 24px ${SEQUENCE_TUTORIAL_ACCENT}66, 0 0 48px ${SEQUENCE_TUTORIAL_ACCENT}22`,
                zIndex: SEQUENCE_TUTORIAL_OVERLAY_Z_INDEX + 1,
                pointerEvents: "none",
                animation: "sequenceSearchTutorialPulse 2s ease-in-out infinite",
              }}
            />
          ) : null}

          <div
            key={tutorialCurrentStep.id}
            style={{
              ...tutorialCardStyle,
              zIndex: SEQUENCE_TUTORIAL_CARD_Z_INDEX,
              background: SEQUENCE_TUTORIAL_CARD_BG,
              border: _isLightTheme
                ? "1px solid #E5E7EB"
                : `1.5px solid ${SEQUENCE_TUTORIAL_ACCENT}66`,
              borderRadius: 20,
              padding: "26px 26px 20px",
              boxShadow: _isLightTheme
                ? "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06)"
                : `0 32px 80px rgba(0,0,0,0.88), 0 0 0 1px ${SEQUENCE_TUTORIAL_ACCENT}22, 0 0 40px ${SEQUENCE_TUTORIAL_ACCENT}18, inset 0 1px 0 ${SEQUENCE_TUTORIAL_ACCENT}22`,
              fontFamily: SEQUENCE_TUTORIAL_FONT_BASE,
            }}
          >
            {tutorialCardArrowSide === "top" ? (
              <div
                style={{
                  position: "absolute",
                  top: -9,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "9px solid transparent",
                  borderRight: "9px solid transparent",
                  borderBottom: `9px solid ${SEQUENCE_TUTORIAL_ACCENT}88`,
                  filter: `drop-shadow(0 -2px 4px ${SEQUENCE_TUTORIAL_ACCENT}44)`,
                }}
              />
            ) : tutorialCardArrowSide === "bottom" ? (
              <div
                style={{
                  position: "absolute",
                  bottom: -9,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "9px solid transparent",
                  borderRight: "9px solid transparent",
                  borderTop: `9px solid ${SEQUENCE_TUTORIAL_ACCENT}88`,
                  filter: `drop-shadow(0 2px 4px ${SEQUENCE_TUTORIAL_ACCENT}44)`,
                }}
              />
            ) : null}

            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
              {sequenceTutorialSteps.map((stepItem, index) => (
                <div
                  key={stepItem.id}
                  style={{
                    height: 3,
                    flex: 1,
                    borderRadius: 99,
                    background:
                      index <= tutorialStep
                        ? SEQUENCE_TUTORIAL_ACCENT
                        : SEQUENCE_TUTORIAL_STEP_INACTIVE,
                    transition: "background 0.4s",
                    boxShadow:
                      index <= tutorialStep
                        ? `0 0 6px ${SEQUENCE_TUTORIAL_ACCENT}88`
                        : "none",
                  }}
                />
              ))}
              <span
                style={{
                  color: _isLightTheme
                    ? SEQUENCE_TUTORIAL_ACCENT
                    : `${SEQUENCE_TUTORIAL_ACCENT}99`,
                  flexShrink: 0,
                  fontFamily: SEQUENCE_TUTORIAL_FONT_MEDIUM,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0,
                  marginLeft: 2,
                }}
              >
                {tutorialStep + 1}/{sequenceTutorialSteps.length}
              </span>
            </div>

            {tutorialCurrentStep.id === "welcome" ? (
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: `linear-gradient(135deg, ${SEQUENCE_TUTORIAL_ACCENT}33, ${SEQUENCE_TUTORIAL_ACCENT}11)`,
                  border: `1px solid ${SEQUENCE_TUTORIAL_ACCENT}55`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                <svg
                  width="28"
                  height="28"
                  viewBox="0 0 28 28"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M5 18.5c4.4 0 4.4-9 8.9-9 4.5 0 4.5 9 9.1 9"
                    fill="none"
                    stroke={SEQUENCE_TUTORIAL_ACCENT}
                    strokeWidth="2.2"
                    strokeLinecap="round"
                  />
                  <circle cx="5" cy="18.5" r="2.4" fill={SEQUENCE_TUTORIAL_ACCENT} />
                  <circle cx="14" cy="9.5" r="2.4" fill={SEQUENCE_TUTORIAL_ACCENT} />
                  <circle cx="23" cy="18.5" r="2.4" fill={SEQUENCE_TUTORIAL_ACCENT} />
                </svg>
              </div>
            ) : null}

            <h2
              style={{
                margin: "0 0 10px",
                fontSize: tutorialCurrentStep.id === "welcome" ? 22 : 18,
                fontWeight: 800,
                fontFamily: SEQUENCE_TUTORIAL_FONT_BOLD,
                color: SEQUENCE_TUTORIAL_TITLE_COLOR,
                lineHeight: 1.25,
                whiteSpace: "pre-line",
                letterSpacing: 0,
              }}
            >
              {tutorialCurrentStep.title}
            </h2>

            <p
              style={{
                margin: "0 0 24px",
                fontSize: 13.5,
                fontFamily: SEQUENCE_TUTORIAL_FONT_BASE,
                color: SEQUENCE_TUTORIAL_BODY_COLOR,
                lineHeight: 1.6,
              }}
            >
              {tutorialCurrentStep.body}
            </p>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
              }}
            >
              <button
                type="button"
                onClick={handleTutorialSkip}
                style={{
                  background: "transparent",
                  border: "none",
                  color: SEQUENCE_TUTORIAL_SKIP_COLOR,
                  fontSize: 13,
                  cursor: "pointer",
                  padding: "6px 0",
                  fontFamily: SEQUENCE_TUTORIAL_FONT_MEDIUM,
                  fontWeight: 500,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                }}
              >
                {t("sequenceSearch.tutorialSkip")}
              </button>

              <button
                type="button"
                onClick={handleTutorialNext}
                style={{
                  background: SEQUENCE_TUTORIAL_ACCENT,
                  border: "none",
                  color: SEQUENCE_TUTORIAL_BTN_TEXT,
                  fontFamily: SEQUENCE_TUTORIAL_FONT_BOLD,
                  fontWeight: 800,
                  fontSize: 13.5,
                  padding: "10px 24px",
                  borderRadius: 10,
                  cursor: "pointer",
                  letterSpacing: 0,
                  boxShadow: `0 4px 20px ${SEQUENCE_TUTORIAL_ACCENT}55`,
                }}
              >
                {tutorialIsLastStep
                  ? t("sequenceSearch.tutorialDone")
                  : t("sequenceSearch.tutorialNext")}
              </button>
            </div>
          </div>

          <style>
            {`
              @keyframes sequenceSearchTutorialPulse {
                0%,100% { box-shadow: 0 0 0 1px ${SEQUENCE_TUTORIAL_ACCENT}44, 0 0 20px ${SEQUENCE_TUTORIAL_ACCENT}55, 0 0 40px ${SEQUENCE_TUTORIAL_ACCENT}22; }
                50% { box-shadow: 0 0 0 2px ${SEQUENCE_TUTORIAL_ACCENT}88, 0 0 32px ${SEQUENCE_TUTORIAL_ACCENT}99, 0 0 64px ${SEQUENCE_TUTORIAL_ACCENT}44; }
              }
            `}
          </style>
        </>
      ) : null}
      <NotificationList notifications={notifications} onClose={removeNotification} />
    </MainWrapper>
  );
};

export default SequenceSearch;

const Page = styled.main`
  flex: 1;
  min-width: 0;
  /* Column layout: the header stays put, PageScroll below it scrolls. */
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 28px 28px 0 0;

  @media only screen and (${breakpoints.mobile}) {
    padding: 20px 20px 0;
  }
`;

const PageScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding-bottom: 60px;

  @media only screen and (${breakpoints.mobile}) {
    padding-bottom: 20px;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  /* Header sits outside PageScroll, so it is always visible; no sticky
     gymnastics needed across the nested overflow contexts. */
  margin-bottom: 24px;
`;

// Title block + action buttons, separated from the tab row by a divider.
const HeaderTop = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  @media only screen and (${breakpoints.mobile}) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const HeaderActions = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;

  @media only screen and (${breakpoints.mobile}) {
    align-items: stretch;
    flex-direction: column;
    width: 100%;
  }
`;

// Row of top-level form tabs; the active tab is marked with a green underline
// that overlaps the row's bottom border.
const Tabs = styled.div`
  display: flex;
  gap: 28px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  @media only screen and (${breakpoints.mobile}) {
    gap: 18px;
    overflow-x: auto;
  }
`;

const Tab = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 12px 2px;
  margin-bottom: -1px;
  text-align: left;
  color: ${({ $active, theme }) => ($active ? theme.green : theme.blueish2)};
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.green : "transparent")};

  &:hover {
    color: ${({ $active, theme }) => ($active ? theme.green : theme.blueish)};
  }
`;

const TabTitle = styled.div`
  font-family: "SatoshiBold";
  font-size: 14px;
  white-space: nowrap;
  color: inherit;
`;

const TabSubtitle = styled.div`
  font-family: "SatoshiMedium";
  font-size: 12px;
  white-space: nowrap;
  margin-top: 2px;
  color: ${({ theme }) => theme.blueish2};
`;

// Compact, horizontal variant of the saved-template controls so they fit in
// the always-visible header row between the title and "How it works".
const HeaderTemplateControls = styled.div`
  display: flex;
  align-items: center;
  min-width: 0;

  ${TemplateControlsSection} {
    width: auto;
  }

  ${TemplateControls} {
    flex-direction: row;
    align-items: center;
    width: auto;
  }

  ${TemplateDropdown} {
    width: auto;
    min-width: 220px;
  }

  ${TemplateDropdownButton} {
    font-family: "SatoshiMedium";
    font-weight: 700;
  }

  ${TemplateDropdownButtonText} > span {
    font-family: "SatoshiMedium";
    font-size: 11px;
    font-weight: 700;
  }

  @media only screen and (${breakpoints.mobile}) {
    width: 100%;

    ${TemplateControlsSection},
    ${TemplateControls},
    ${TemplateDropdown} {
      width: 100%;
    }

    ${TemplateControls} {
      flex-direction: column;
      align-items: stretch;
    }
  }
`;

// Matches the shared template buttons (PanelToggleButton) so the three header
// actions read as one uniform group.
const HowItWorksButton = styled.button`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.inputBg};
  color: ${({ theme }) => theme.white};
  cursor: pointer;
  font-family: "SatoshiMedium";
  font-weight: 700;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  min-height: 40px;
  padding: 10px 12px;
  white-space: nowrap;
  position: relative;
  z-index: ${SEQUENCE_HOW_IT_WORKS_Z_INDEX};

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

const Eyebrow = styled.div`
  color: ${({ theme }) => theme.green};
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 6px 0 0;
  color: ${({ theme }) => theme.blueish};
  font-family: "SpaceGrotesk";
  font-size: 30px;
  line-height: 36px;
`;

const ResultCount = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.blueish};
  padding: 9px 12px;
  font-family: "SatoshiBold";
  font-size: 13px;
`;

// Single column: the form stacks above its results.
const Workspace = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 18px;
`;

const FormPanel = styled.form`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.sidebar};
  padding: 18px;
`;

const ResultsPanel = styled.section`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.sidebar};
  padding: 18px;
  align-self: start;
`;

const RecruitmentPageHead = styled.div`
  padding: 10px 0 0;
  h2 { color: ${({ theme }) => theme.blueish}; font-family: "SpaceGrotesk"; font-size: 30px; margin: 0; }
  p { color: ${({ theme }) => theme.blueish2}; font-size: 15px; margin: 6px 0 16px; }
`;

const RecruitmentStepper = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 16px;
  background: ${({ theme }) => theme.sidebar};
  @media only screen and (${breakpoints.mobile}) { grid-template-columns: 1fr; }
`;

const RecruitmentStepperItem = styled.div`
  align-items: flex-start;
  background: ${({ theme, $active }) => ($active ? theme.accentBg : "transparent")};
  border-right: 1px solid ${({ theme }) => theme.borderColor};
  color: ${({ theme, $active, $done }) => ($active ? theme.green : $done ? theme.blueish : theme.blueish2)};
  display: flex;
  gap: 14px;
  min-height: 88px;
  padding: 20px;
  &:last-child { border-right: 0; }
  b, ${''}span { display: block; }
  b { align-items: center; border: 1px solid currentColor; border-radius: 50%; display: flex; flex: none; font-size: 13px; height: 30px; justify-content: center; width: 30px; }
  strong { font-family: "SatoshiBold"; font-size: 16px; }
  small { color: ${({ theme }) => theme.blueish2}; display: block; font-size: 13px; margin-top: 4px; }
`;

const RecruitmentBuilderPanel = styled.section`
  border: 1px solid ${({ theme }) => theme.green};
  border-radius: 14px;
  margin-bottom: 24px;
  padding: 22px;
`;
const RecruitmentStepEyebrow = styled.div`
  align-items: center; color: ${({ theme }) => theme.green}; display: flex; font-family: "SatoshiBold"; font-size: 12px; gap: 8px; text-transform: uppercase;
  span { color: ${({ theme }) => theme.blueish2}; margin-left: auto; text-transform: none; }
`;
const RecruitmentScopeEyebrow = styled(RecruitmentStepEyebrow)` margin-bottom: 10px; `;
const RecruitmentStepNumber = styled.span`
  align-items: center; border: 1px solid currentColor; border-radius: 50%; display: inline-flex; height: 26px; justify-content: center; width: 26px;
`;
const RecruitmentPanelTitle = styled.h2` color: ${({ theme }) => theme.blueish}; font-family: "SatoshiBold"; font-size: 21px; margin: 14px 0 4px; `;
const RecruitmentPanelSubtitle = styled.p` color: ${({ theme }) => theme.blueish2}; font-size: 13px; margin: 0 0 18px; `;
const RecruitmentCardRow = styled.div` display: flex; gap: 14px; overflow-x: auto; padding-bottom: 4px; `;
const RecruitmentSequenceCard = styled.div`
  background: ${({ theme }) => theme.black}; border: 1px solid ${({ theme }) => theme.borderColor}; border-radius: 12px; flex: 0 0 310px; padding: 14px;
`;
const RecruitmentCardHead = styled.div` align-items: center; display: flex; gap: 8px; justify-content: space-between; `;
const RecruitmentSequenceName = styled.input`
  background: transparent; border: 0; border-bottom: 1px solid transparent; color: ${({ theme }) => theme.blueish}; font-family: "SatoshiBold"; font-size: 15px; min-width: 0; outline: none; width: 100%;
  &:focus { border-bottom-color: ${({ theme }) => theme.green}; }
`;
const RecruitmentIconButton = styled.button` background: transparent; border: 0; color: ${({ theme }) => theme.blueish2}; cursor: pointer; font-size: 19px; `;
const RecruitmentEventRow = styled.div` align-items: center; display: flex; flex-wrap: wrap; gap: 6px; min-height: 46px; padding: 12px 0; `;
const RecruitmentEventChip = styled.button`
  background: ${({ theme }) => theme.lines}; border: 1px solid ${({ theme }) => theme.borderColor}; border-radius: 6px; color: ${({ theme }) => theme.blueish}; cursor: pointer; font-size: 12px; padding: 6px 8px;
  small { color: ${({ theme }) => theme.blueish2}; margin-left: 5px; }
`;
const RecruitmentArrow = styled.span` color: ${({ theme }) => theme.blueish2}; `;
const RecruitmentEventSelect = styled.select`
  background: ${({ theme }) => theme.black}; border: 1px dashed ${({ theme }) => theme.borderColor}; border-radius: 6px; color: ${({ theme }) => theme.blueish}; padding: 7px; width: 100%;
`;
const RecruitmentIncludeLabel = styled.label` color: ${({ theme }) => theme.blueish2}; display: block; font-size: 12px; margin-top: 12px; input { accent-color: ${({ theme }) => theme.green}; margin-right: 6px; } `;
const RecruitmentAddCard = styled.button`
  background: transparent; border: 1px dashed ${({ theme }) => theme.borderColor}; border-radius: 12px; color: ${({ theme }) => theme.blueish2}; cursor: pointer; flex: 0 0 180px; font-family: "SatoshiBold";
  span { display: block; font-size: 24px; } &:hover { border-color: ${({ theme }) => theme.green}; color: ${({ theme }) => theme.green}; } &:disabled { cursor: not-allowed; opacity: .4; }
`;

const Section = styled.section`
  margin-bottom: 24px;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
`;

const SectionHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ResetStepsButton = styled.button`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 12px;
  padding: 6px 12px;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 12px;
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiBold";
  font-size: 16px;
`;

// Always-visible results header. Sticks to the top of PageScroll (the page's
// scroll container) so the totals stay on screen while the result list scrolls.
// Negative margins cancel the ResultsPanel padding so the band spans edge to
// edge and masks content scrolling beneath it.
const ResultsStickyHeader = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin: -18px -18px 14px;
  padding: 14px 18px;
  /* Opaque surface — theme.sidebar is translucent in dark mode (#18191845),
     which let the scrolling result list bleed through the sticky band. */
  background: ${({ theme }) => theme.cardBg};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px 8px 0 0;

  ${SectionTitle} {
    margin: 0;
  }
`;

const ResultsTotals = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ResultsTotalStat = styled.div`
  align-items: baseline;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  display: flex;
  gap: 6px;
  padding: 6px 10px;

  span {
    color: ${({ theme }) => theme.blueish2};
    font-size: 11px;
    letter-spacing: 0.03em;
    text-transform: uppercase;
  }

  strong {
    color: ${({ theme }) => theme.blueish};
    font-family: "SatoshiBold";
    font-size: 15px;
  }
`;

const StepSummary = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-size: 13px;
`;

// Match-option fields, four across to match the scope row. The small option
// controls (video mode, immediate, max-events, chain reps) pack into one row
// instead of stretching, which keeps the form short.
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;

  @media only screen and (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

// Shared scope row: Competition, Season, Teams, Player sit four across on wide
// screens, collapsing to two then one as width drops.
const ScopeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 14px;

  @media only screen and (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media only screen and (${breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
`;

const InlineFieldRow = styled.div`
  display: flex;
  gap: 10px;

  & > * {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-family: "SatoshiRegular";
  font-size: 12px;
`;

const SearchBox = styled.div`
  position: relative;
`;

const TextInput = styled.input`
  width: 100%;
  min-height: 42px;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.blueish};
  font-size: 14px;
  padding: 10px 12px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.green};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.lines};
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
`;

const OptionsList = styled.div`
  position: absolute;
  z-index: 20;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  max-height: 220px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.black};
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.26);
`;

const OptionItem = styled.button`
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.blueish2 : theme.blueish};
  cursor: ${({ $disabled }) => ($disabled ? "default" : "pointer")};
  font-size: 14px;
  padding: 10px 12px;
  text-align: left;

  &:hover {
    background: ${({ theme, $disabled }) =>
      $disabled ? "transparent" : theme.lines};
  }
`;

const DropdownButton = styled.button`
  align-items: center;
  background: ${({ theme }) => theme.black};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
  display: flex;
  font-family: "SatoshiRegular";
  font-size: 14px;
  gap: 10px;
  justify-content: space-between;
  min-height: 42px;
  padding: 10px 12px;
  text-align: left;
  width: 100%;

  span:first-child {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const DropdownOption = styled(OptionItem)`
  background: ${({ theme, $selected }) =>
    $selected ? theme.lines : "transparent"};
  color: ${({ theme, $selected, $disabled }) =>
    $disabled ? theme.blueish2 : $selected ? theme.green : theme.blueish};
  font-family: ${({ $selected }) =>
    $selected ? "SatoshiBold" : "SatoshiRegular"};
`;

const DateRangeField = styled.div`
  /* Shares the top options row with Pre-set + Matches on wide screens; spans
     the full width once the grid collapses. */
  grid-column: span 2;
  min-width: 0;

  @media only screen and (${breakpoints.mobile}) {
    grid-column: 1 / -1;
  }
`;

const ModeToggleGroup = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  min-height: 42px;
  overflow: hidden;
`;

const ModeToggleButton = styled.button`
  background: ${({ theme, $selected }) =>
    $selected ? theme.green : theme.black};
  border: 0;
  border-right: 1px solid ${({ theme }) => theme.borderColor};
  color: ${({ theme, $selected }) => ($selected ? theme.black : theme.blueish)};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 13px;
  padding: 9px 10px;

  &:last-child {
    border-right: 0;
  }
`;

const ImmediateToggleButton = styled.button`
  align-items: center;
  background: ${({ theme, $active }) => ($active ? theme.green : theme.black)};
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.green : theme.borderColor)};
  border-radius: 6px;
  color: ${({ theme, $active }) => ($active ? theme.black : theme.blueish)};
  cursor: pointer;
  display: flex;
  font-family: "SatoshiBold";
  font-size: 13px;
  justify-content: center;
  min-height: 42px;
  padding: 9px 12px;
`;

const FieldHint = styled.div`
  color: ${({ theme }) => theme.blueish};
  font-size: 12px;
`;

const ErrorText = styled.div`
  color: ${({ theme }) => theme.red};
  font-size: 12px;
`;

// Teaching empty state shown when no steps exist yet.
const TimelineExampleHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  border: 1px dashed ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.blueish2};
  font-size: 13px;
  padding: 10px 14px;
  margin-bottom: 12px;
`;

const ExampleDropdown = styled.div`
  position: relative;
`;

const LoadExampleButton = styled.button`
  border: 1px solid ${({ theme }) => theme.green};
  border-radius: 6px;
  background: ${({ theme, $open }) => ($open ? theme.green : "transparent")};
  color: ${({ theme, $open }) => ($open ? theme.black : theme.green)};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 12px;
  padding: 6px 12px;
  white-space: nowrap;

  &:hover {
    background: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.black};
  }
`;

const ExampleMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 30;
  min-width: 160px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.cardBg};
  padding: 6px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
`;

const ExampleMenuItem = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
  font-family: "SatoshiMedium";
  font-size: 13px;
  padding: 8px 10px;

  &:hover {
    background: ${({ theme }) => theme.lines};
    color: ${({ theme }) => theme.green};
  }
`;

// Steps are cards laid out left-to-right and wrap, connected by arrows; each card
// is a self-contained "object", which suits frequent drag-reordering.
const StepList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 14px;
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 200px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.black};
  padding: 12px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`;

// Connector between consecutive step cards; vertically centered in the row.
const StepArrow = styled.div`
  align-self: center;
  color: ${({ theme }) => theme.blueish2};
  font-size: 20px;
  line-height: 1;
  user-select: none;
`;

const StepCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StepOrder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => theme.green};
  color: ${({ theme }) => theme.black};
  font-family: "SatoshiBold";
  font-size: 13px;
`;

const StepSource = styled.div`
  color: ${({ theme }) => theme.green};
  font-family: "SatoshiBold";
  font-size: 16px;
  margin-bottom: 4px;
`;

const StepMeta = styled.div`
  color: ${({ theme }) => theme.blueish2};
  font-size: 12px;
  margin-bottom: 8px;
`;

const StepGrid = styled.div`
  display: flex;
  gap: 8px;
`;

const TeamChoiceButton = styled.button`
  flex: 1;
  min-height: 36px;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.green : theme.borderColor)};
  border-radius: 6px;
  background: ${({ theme, $active }) => ($active ? theme.green : theme.black)};
  color: ${({ theme, $active }) => ($active ? theme.black : theme.blueish)};
  cursor: pointer;
  font-family: "SatoshiBold";
  padding: 0 12px;
`;

const StepCheckboxLabel = styled.label`
  align-items: center;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  display: inline-flex;
  font-family: "SatoshiMedium";
  font-size: 12px;
  gap: 6px;

  input {
    accent-color: ${({ theme }) => theme.green};
    cursor: pointer;
    margin: 0;
  }
`;

// Trailing placeholder card; opens the event-picker popover so a step can be added.
const AddStepWrapper = styled.div`
  position: relative;
  width: 200px;
`;

const AddStepCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  min-height: 130px;
  border: 1px dashed
    ${({ theme, $open }) => ($open ? theme.green : theme.borderColor)};
  border-radius: 8px;
  background: transparent;
  color: ${({ theme, $open }) => ($open ? theme.green : theme.blueish2)};
  cursor: pointer;
  font-family: "SatoshiMedium";
  font-size: 13px;

  span:first-child {
    font-size: 24px;
    line-height: 1;
  }

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

// Floating event search anchored under the Add step card. cardBg (not the
// translucent sidebar token) keeps the overlay opaque.
const EventPickerPopup = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 60;
  width: 320px;
  max-width: 80vw;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.cardBg};
  padding: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
`;

const RemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
`;

// Active-scope summary; sits on the left of the action row.
const ScopeSummaryChip = styled.span`
  margin-right: auto;
  display: inline-flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  color: ${({ theme }) => theme.blueish};
  font-size: 12px;
  padding: 6px 12px;
`;

const SecondaryButton = styled.button`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  background: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
  font-family: "SatoshiBold";
  min-height: 40px;
  padding: 0 14px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.green};
  color: ${({ theme }) => theme.black};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 14px;
  min-height: 44px;
  padding: 0 26px;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const ErrorBanner = styled.div`
  border: 1px solid ${({ theme }) => theme.red};
  border-radius: 6px;
  color: ${({ theme }) => theme.red};
  margin-bottom: 14px;
  padding: 10px 12px;
`;

const EmptyState = styled.div`
  border: 1px dashed ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  color: ${({ theme }) => theme.blueish2};
  padding: 24px;
  text-align: center;
`;

const ResultGroup = styled.article`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  background: ${({ theme }) => theme.black};
  margin-bottom: 12px;
  padding: 14px;
`;

const PaginationBar = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 14px;

  @media only screen and (${breakpoints.mobile}) {
    align-items: stretch;
    flex-direction: column;
  }
`;

const PaginationStatus = styled.div`
  color: ${({ theme }) => theme.blueish2};
  font-size: 13px;
  min-width: 88px;
  text-align: center;
`;

const ResultHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
`;

const ResultTitle = styled.h3`
  margin: 0 0 4px;
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiBold";
  font-size: 15px;
`;

const ResultMeta = styled.div`
  color: ${({ theme }) => theme.blueish2};
  font-size: 12px;
`;

const SequencePill = styled.div`
  white-space: nowrap;
  border: 1px solid ${({ theme }) => theme.green};
  border-radius: 999px;
  color: ${({ theme }) => theme.green};
  font-size: 12px;
  padding: 5px 9px;
`;

const RecruitmentTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    color: ${({ theme }) => theme.blueish2};
    font-size: 11px;
    font-family: "SatoshiBold";
    letter-spacing: 0.05em;
    padding: 8px;
    text-align: left;
    text-transform: uppercase;
  }

  td { padding: 10px 8px; }
  th:nth-child(3), td:nth-child(3) { text-align: right; }
  th:last-child, td:last-child { width: 28px; text-align: right; }
`;

const RecruitmentPlayerRow = styled.tr`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  color: ${({ theme, $open }) => ($open ? theme.green : theme.blueish)};
  cursor: pointer;

  &:hover { color: ${({ theme }) => theme.green}; }
`;

const RecruitmentOccurrenceList = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  display: grid;
  gap: 8px;
  padding: 10px 0 4px;
`;

const RecruitmentOccurrence = styled.div`
  align-items: center;
  color: ${({ theme }) => theme.blueish2};
  display: flex;
  font-size: 12px;
  gap: 12px;
  justify-content: space-between;
`;

const SeqChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 8px 0;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
  margin-bottom: 12px;
`;

const SeqChipsLabel = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-right: 4px;
`;

const SeqChip = styled.button`
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.green : theme.borderColor)};
  border-radius: 4px;
  background: ${({ theme, $active }) =>
    $active ? theme.accentBg : "transparent"};
  color: ${({ theme, $active }) => ($active ? theme.green : theme.blueish2)};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 11px;
  padding: 3px 9px;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

const SequenceBlock = styled.div`
  margin-top: 4px;
`;

// Opponent Pattern Analysis: one repeated pattern (signature + repetition count
// + its occurrences).
const PatternBlock = styled.div`
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  margin-top: 12px;
  padding-top: 12px;
`;

const PatternSummary = styled.div`
  color: ${({ theme }) => theme.blueish};
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 10px;
`;

const PatternHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const RepeatBadge = styled.div`
  white-space: nowrap;
  border: 1px solid ${({ theme }) => theme.green};
  border-radius: 999px;
  background: ${({ theme }) => theme.accentBg};
  color: ${({ theme }) => theme.green};
  font-family: "SatoshiBold";
  font-size: 13px;
  padding: 4px 12px;
`;

const OccurrenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
`;

const OccurrenceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  padding: 8px 10px;
`;

const OccurrenceLabel = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-size: 12px;
`;

const SequenceRowHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SequenceRowTitle = styled.h4`
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiBold";
  font-size: 14px;
  margin: 0;
`;

const SequenceRowActions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`;

const MiniStat = styled.span`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  color: ${({ theme }) => theme.blueish2};
  font-size: 12px;
  padding: 5px 8px;
  white-space: nowrap;
`;

const OpenEventsButton = styled.button`
  border: 1px solid ${({ theme }) => theme.green};
  border-radius: 6px;
  background: ${({ theme }) => theme.green};
  color: ${({ theme }) => theme.black};
  cursor: pointer;
  font-family: "SatoshiBold";
  font-size: 12px;
  min-height: 30px;
  padding: 0 10px;
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const StepsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
`;

const StepPill = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 5px;
  background: ${({ theme }) => theme.black};
  color: ${({ theme }) => theme.blueish};
  font-size: 12px;
  padding: 5px 10px;
  opacity: ${({ $excluded }) => ($excluded ? 0.55 : 1)};
`;

const StepPillNum = styled.span`
  align-items: center;
  background: ${({ theme }) => theme.green};
  border-radius: 50%;
  color: ${({ theme }) => theme.black};
  display: flex;
  flex-shrink: 0;
  font-family: "SatoshiBold";
  font-size: 9px;
  height: 18px;
  justify-content: center;
  width: 18px;
`;

const StepPillLabel = styled.span`
  white-space: nowrap;
`;

const StepPillCount = styled.span`
  color: ${({ theme }) => theme.blueish2};
  font-size: 10px;
`;

const StepPillArrow = styled.span`
  color: ${({ theme }) => theme.borderColor};
  font-size: 14px;
`;

const StepExcludedBadge = styled.span`
  background: ${({ theme }) => theme.lines};
  border-radius: 4px;
  color: ${({ theme }) => theme.blueish2};
  font-family: "Satoshi Medium";
  font-size: 10px;
  letter-spacing: 0.04em;
  margin-left: 2px;
  padding: 1px 6px;
  text-transform: uppercase;
`;

const ResultSummaryGrid = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  margin-bottom: 10px;
`;

const ResultSummaryItem = styled.div`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  padding: 8px;

  strong {
    color: ${({ theme }) => theme.blueish2};
    display: block;
    font-size: 11px;
    margin-bottom: 2px;
  }

  span {
    color: ${({ theme }) => theme.blueish};
    font-family: "SatoshiBold";
    font-size: 14px;
  }

  small {
    color: ${({ theme }) => theme.blueish2};
    display: block;
    font-size: 11px;
    margin-top: 2px;
  }
`;

const MarkerListSection = styled.div`
  margin-bottom: 10px;
`;

const MarkersToggle = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme, $open }) => ($open ? theme.blueish : theme.blueish2)};
  font-family: "SatoshiBold";
  font-size: 12px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 0;
  margin-bottom: 8px;

  &:hover {
    color: ${({ theme }) => theme.blueish};
  }
`;

const MarkersChevron = styled.span`
  display: inline-block;
  transition: transform 0.2s;
  transform: rotate(${({ $open }) => ($open ? "180deg" : "0deg")});
`;

const MarkerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const MarkerListItem = styled.div`
  align-items: center;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  display: grid;
  gap: 8px;
  grid-template-columns: minmax(0, 1fr) 28px;
  opacity: ${({ $excluded }) => ($excluded ? 0.48 : 1)};
  padding: 8px 10px;
`;

const MarkerListContent = styled.div`
  min-width: 0;
`;

const MarkerAction = styled.div`
  color: ${({ theme }) => theme.blueish};
  font-family: "SatoshiBold";
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MarkerMeta = styled.div`
  color: ${({ theme }) => theme.blueish2};
  font-size: 12px;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const MarkerExcludedText = styled.div`
  color: ${({ theme }) => theme.red};
  font-size: 11px;
  line-height: 16px;
`;

const MarkerRemoveButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 6px;
  color: ${({ theme }) => theme.blueish};
  cursor: pointer;
  display: flex;
  font-family: "SatoshiBold";
  height: 28px;
  justify-content: center;
  padding: 0;
  width: 28px;

  &:hover {
    border-color: ${({ theme }) => theme.green};
    color: ${({ theme }) => theme.green};
  }
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 999px;
  color: ${({ theme }) => theme.blueish};
  font-size: 12px;
  padding: 4px 8px;
`;

const PlayerFieldStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
  min-width: 0;
`;

const PlayerChip = styled(Chip)`
  align-items: center;
  display: inline-flex;
  gap: 6px;
`;

const PlayerChipRemoveButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.blueish2};
  cursor: pointer;
  display: inline-flex;
  font-family: "SatoshiBold";
  font-size: 12px;
  justify-content: center;
  line-height: 1;
  padding: 0;

  &:hover {
    color: ${({ theme }) => theme.red};
  }
`;
